<template>
  <div class="page-shell home-mode studio-mode">
    <div class="home-ambient home-ambient-left"></div>
    <div class="home-ambient home-ambient-right"></div>

    <div class="app-shell">
      <AppSidebar />

      <main
        class="studio-shell"
        :class="{
          'studio-shell-flow': mode !== 'text',
          'studio-shell-text': mode === 'text',
          'studio-shell-sidebar-collapsed': !isStudioSidebarExpanded
        }"
      >
        <aside class="studio-sidebar" :class="{ 'is-collapsed': !isStudioSidebarExpanded }">
          <button
            type="button"
            class="studio-sidebar-collapse-toggle"
            :aria-expanded="String(isStudioSidebarExpanded)"
            @click="toggleStudioSidebar"
          >
            {{ isStudioSidebarExpanded ? "收起" : "展开" }}
          </button>

          <div v-if="isStudioSidebarExpanded" class="studio-sidebar-content">
          <div class="studio-brand">
            <button
              type="button"
              class="studio-brand-toggle"
              @click="toggleStudioSidebar"
            >
              <div class="studio-brand-copy">
                <p class="studio-eyebrow">{{ currentConfig.brand }}</p>
                <h1>{{ currentConfig.title }}</h1>
              </div>
              <span class="studio-brand-toggle-icon">⌃</span>
            </button>
            <p class="studio-brand-subtitle">{{ currentConfig.subtitle }}</p>
          </div>

          <button
            type="button"
            class="studio-primary-action"
            :disabled="(mode === 'text' && creatingConversation) || (mode === 'picture' && generatingImage) || (mode === 'video' && generatingVideo) || (mode === 'speech' && generatingSpeech)"
            @click="handlePrimaryAction"
          >
            {{ primaryActionLabel }}
          </button>

          <div class="studio-history">
            <div class="studio-block-title">
              <span>{{ mode === "text" ? "历史会话" : "历史项目" }}</span>
            </div>

            <div v-if="mode === 'text' && loadingHistory" class="studio-history-empty">
              正在加载会话...
            </div>

            <div v-else-if="mode === 'text' && !textHistory.length" class="studio-history-empty">
              暂无历史会话
            </div>

            <div v-else-if="mode === 'picture' && loadingImageHistory" class="studio-history-empty">
              正在加载图片任务...
            </div>

            <div v-else-if="mode === 'picture' && !imageHistory.length" class="studio-history-empty">
              暂无图片任务
            </div>

            <div v-else-if="mode === 'video' && loadingVideoHistory" class="studio-history-empty">
              正在加载视频任务...
            </div>

            <div v-else-if="mode === 'video' && !videoHistory.length" class="studio-history-empty">
              暂无视频任务
            </div>

            <div v-else-if="mode === 'speech' && loadingSpeechHistory" class="studio-history-empty">
              正在加载语音任务...
            </div>

            <div v-else-if="mode === 'speech' && !speechHistory.length" class="studio-history-empty">
              暂无语音任务
            </div>

            <div
              v-for="item in sidebarHistory"
              :key="item.id"
              class="studio-history-row"
              :class="{ 'is-active': isActiveHistory(item) }"
            >
              <button
                type="button"
                class="studio-history-item"
                :class="{ 'is-active': isActiveHistory(item) }"
                @click="handleHistoryClick(item)"
              >
                <strong>{{ item.title }}</strong>
                <span>{{ item.time }}</span>
              </button>

              <button
                type="button"
                class="studio-history-delete"
                :disabled="deletingHistoryId === String(item.id)"
                :aria-label="'删除' + item.title"
                @click.stop="handleDeleteHistory(item)"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>

          <div class="studio-assist">
            <div class="studio-block-title">
              <span>快捷灵感</span>
            </div>
            <div class="studio-chip-list">
              <button
                v-for="chip in currentConfig.chips"
                :key="chip"
                type="button"
                class="studio-chip"
                @click="applyChip(chip)"
              >
                {{ chip }}
              </button>
            </div>
          </div>
          </div>
        </aside>

        <section class="studio-main">
          <header v-if="mode !== 'text' && mode !== 'picture' && mode !== 'video'" class="studio-header">
            <div>
              <p class="studio-eyebrow">Creative Workspace</p>
              <h2>{{ currentConfig.header }}</h2>
            </div>

            <div class="studio-tabs">
              <router-link class="studio-tab" :class="{ 'is-active': mode === 'text' }" :to="{ name: 'chat-text' }">
                文本
              </router-link>
              <router-link class="studio-tab" :class="{ 'is-active': mode === 'picture' }" :to="{ name: 'chat-picture' }">
                图片
              </router-link>
              <router-link class="studio-tab" :class="{ 'is-active': mode === 'video' }" :to="{ name: 'chat-video' }">
                视频
              </router-link>
            </div>
          </header>

          <div class="studio-stage studio-stage-wide">
            <section v-if="mode === 'text'" class="studio-panel studio-panel-main studio-panel-text">
              <div v-if="textStatus" class="studio-status-text">{{ textStatus }}</div>

              <div ref="conversationBody" class="studio-conversation">
                <article
                  v-for="message in textMessages"
                  :key="message.id"
                  class="studio-message"
                  :class="{ 'studio-message-user': message.role === 'user', 'studio-message-streaming': message.id === streamingMessageId }"
                >
                  <div class="studio-message-meta">
                    <span>{{ message.role === "user" ? "你" : "AI 助手" }}</span>
                    <small>{{ message.time }}</small>
                    <small v-if="message.role === 'assistant' && message.pointsCost">{{ formatPointsCost(message.pointsCost) }}</small>
                  </div>
                  <div class="studio-bubble">
                    <div class="studio-markdown" v-html="renderMessageContent(message)"></div>
                    <span v-if="message.id === streamingMessageId" class="studio-typing-cursor"></span>
                  </div>
                </article>

                <div v-if="loadingConversation" class="studio-history-empty studio-history-empty-inline">
                  正在恢复会话...
                </div>

                <div
                  v-if="!loadingConversation && !textMessages.length"
                  class="studio-history-empty studio-history-empty-inline"
                >
                  输入你的第一个问题，开始新的文本会话。
                </div>
                <div ref="conversationBottom" class="studio-scroll-anchor"></div>
              </div>

              <div class="studio-composer">
                <textarea
                  v-model="draft"
                  class="studio-textarea"
                  :placeholder="currentConfig.placeholder"
                ></textarea>
                <div class="studio-composer-bar">
                  <div class="studio-composer-meta">
                    <span>{{ currentConversationHint }}</span>
                    <small>{{ scenePointCostText("ai_chat") }}</small>
                  </div>
                  <div class="studio-composer-actions">
                    <button
                      type="button"
                      class="studio-submit"
                      :disabled="sendingMessage || creatingConversation || !trimmedDraft"
                      @click="handleSendTextMessage"
                    >
                      {{ sendingMessage ? "生成中..." : currentConfig.actionLabel }}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section v-else-if="mode === 'picture'" class="studio-panel studio-panel-main studio-panel-picture">
              <div ref="imageBody" class="studio-picture-body">
                <div v-if="imageStatus" class="studio-status-text">{{ imageStatus }}</div>

                <div class="studio-gallery-header">
                  <div>
                    <p class="studio-eyebrow">Gallery</p>
                    <h3>图像生成结果</h3>
                  </div>
                  <span class="studio-meta-note">{{ pictureMetaNote }}</span>
                </div>

                <div v-if="imageLoadingDetail" class="studio-history-empty studio-history-empty-inline">
                  正在加载图片详情...
                </div>

                <div v-else-if="currentImageTask && currentImageTask.status === 'failed'" class="studio-history-empty studio-history-empty-inline">
                  {{ currentImageTask.errorMessage || "图片生成失败，请重新尝试。" }}
                </div>

                <div v-else-if="currentImageAssets.length" class="studio-picture-grid">
                  <article
                    v-for="item in currentImageAssets"
                    :key="item.id"
                    class="studio-picture-card"
                  >
                    <button
                      type="button"
                      class="studio-picture-preview-trigger"
                      @click="openImagePreview(item)"
                    >
                      <div class="studio-picture-frame studio-picture-frame-image">
                        <img
                          class="studio-picture-image"
                          :src="item.src"
                          :alt="item.title"
                        >
                        <div class="studio-picture-overlay">
                          <span>{{ item.tag }}</span>
                        </div>
                      </div>
                    </button>
                    <div class="studio-picture-copy">
                      <strong>{{ item.title }}</strong>
                      <p>{{ item.prompt }}</p>
                      <div class="studio-card-actions">
                        <button
                          type="button"
                          class="studio-visibility-toggle"
                          @click="toggleImageAssetVisibility(item)"
                        >
                          {{ item.isPublic ? "隐藏" : "公开" }}
                        </button>
                        <span v-if="item.pointsCost" class="studio-points-cost">{{ formatPointsCost(item.pointsCost) }}</span>
                      </div>
                    </div>
                  </article>
                </div>

                <div v-else class="studio-history-empty studio-history-empty-inline">
                  输入提示词后开始生成图片。
                </div>
                <div ref="imageBottom" class="studio-scroll-anchor"></div>
              </div>

              <div class="studio-composer studio-composer-inline">
                <div class="studio-video-reference-block studio-image-reference-block">
                  <div class="studio-video-reference-head">
                    <strong>参考图片</strong>
                    <span>可上传 1 张参考图，也可以直接从图片历史中选择。</span>
                  </div>
                  <div class="studio-image-reference-toolbar">
                    <label class="ghost-button studio-reference-button studio-reference-upload-button">
                      上传参考图
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        class="studio-reference-file-input studio-reference-file-input-hidden"
                        @change="handleImageReferenceFileChange(0, $event)"
                      >
                    </label>
                    <button
                      type="button"
                      class="ghost-button studio-reference-button"
                      @click="openImageReferencePicker(0)"
                    >
                      从图片历史选择
                    </button>
                    <button
                      v-if="imageReferenceSlots[0] && imageReferenceSlots[0].url"
                      type="button"
                      class="ghost-button studio-reference-button studio-reference-button-muted"
                      @click="clearImageReferenceUrl(0)"
                    >
                      清空参考图
                    </button>
                    <span v-if="imageReferenceUploading[0]" class="studio-reference-uploading">上传中...</span>
                  </div>
                  <div class="studio-video-reference-grid studio-image-reference-grid">
                    <div
                      v-for="(item, index) in imageReferenceSlots"
                      :key="'image-reference-slot-' + index"
                      class="studio-video-reference-card"
                    >
                      <div class="studio-video-reference-preview">
                        <img
                          v-if="item.url"
                          :src="item.url"
                          :alt="'参考图片 ' + (index + 1)"
                          class="studio-video-reference-image"
                        >
                        <span v-else>上传参考图片</span>
                      </div>
                    </div>
                  </div>
                </div>
                <textarea
                  v-model="draft"
                  class="studio-textarea"
                  :placeholder="currentConfig.placeholder"
                ></textarea>
                <div class="studio-composer-bar studio-image-submit-row">
                  <div class="studio-image-submit-controls">
                    <select v-model="imageSize" class="studio-inline-select studio-image-size-select">
                      <option v-for="size in imageSizeOptions" :key="size.value" :value="size.value">{{ size.label }}</option>
                    </select>
                    <button
                      type="button"
                      class="studio-submit"
                      :disabled="generatingImage || imageReferenceUploadingState || !trimmedDraft"
                      @click="handleGenerateImage"
                    >
                      {{ generatingImage ? "生成中..." : currentConfig.actionLabel }}
                    </button>
                  </div>
                  <div class="studio-composer-meta studio-image-submit-meta">
                    <span>{{ pictureHint }}</span>
                    <small>{{ scenePointCostText("ai_image") }}</small>
                  </div>
                </div>
                <div class="studio-image-style-block">
                  <div class="studio-video-reference-head">
                    <strong>风格</strong>
                    <button
                      v-if="selectedImageStyleId"
                      type="button"
                      class="studio-style-clear"
                      @click="selectedImageStyleId = ''"
                    >
                      不使用风格
                    </button>
                  </div>
                  <div class="studio-image-style-grid">
                    <button
                      v-for="style in imageStyleOptions"
                      :key="style.id"
                      type="button"
                      class="studio-image-style-card"
                      :class="{ 'is-active': selectedImageStyleId === style.id }"
                      @click="selectedImageStyleId = style.id"
                    >
                      <span class="studio-image-style-frame">
                        <img :src="style.image" :alt="style.name">
                      </span>
                      <strong>{{ style.name }}</strong>
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section v-else-if="mode === 'video'" class="studio-panel studio-panel-main studio-panel-picture">
              <div ref="videoBody" class="studio-picture-body">
                <div v-if="videoStatus" class="studio-status-text">{{ videoStatus }}</div>

                <div class="studio-gallery-header">
                  <div>
                    <p class="studio-eyebrow">Storyboard</p>
                    <h3>视频生成结果</h3>
                  </div>
                  <span class="studio-meta-note">{{ videoMetaNote }}</span>
                </div>

                <div v-if="videoLoadingDetail" class="studio-history-empty studio-history-empty-inline">
                  正在加载视频详情...
                </div>

                <div v-else-if="currentVideoMessages.length" class="studio-video-list">
                  <article
                    v-for="item in currentVideoMessages"
                    :key="item.id"
                    class="studio-video-card"
                  >
                    <div v-if="item.videoUrl" class="studio-video-frame studio-video-frame-media">
                      <video
                        class="studio-video-player"
                        :key="item.videoUrl"
                        controls
                        playsinline
                        preload="metadata"
                        @error="handleVideoPlaybackError(item)"
                      >
                        <source :src="item.videoUrl" :type="resolveVideoMimeType(item.videoUrl)">
                        当前浏览器不支持视频播放。
                      </video>
                      <div v-if="isVideoPlaybackError(item)" class="studio-video-error">
                        <span>当前浏览器无法播放该视频，可能是视频编码不兼容。</span>
                        <a :href="item.videoUrl" target="_blank" rel="noopener noreferrer">新窗口打开视频</a>
                      </div>
                    </div>
                    <div v-else class="studio-history-empty studio-history-empty-inline studio-video-placeholder">
                      {{ item.statusText }}
                    </div>
                      <div class="studio-video-copy">
                        <div class="studio-video-title-row">
                          <strong>{{ item.title }}</strong>
                          <span>
                            {{ item.durationLabel }}
                          </span>
                        </div>
                        <p>{{ item.prompt }}</p>
                        <div class="studio-card-actions">
                          <a
                            v-if="item.videoUrl"
                            class="studio-video-download"
                            :href="item.videoUrl"
                            :download="buildVideoDownloadName(item)"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            下载视频
                          </a>
                          <button
                            type="button"
                            class="studio-visibility-toggle"
                            @click="toggleVideoMessageVisibility(item)"
                          >
                            {{ item.isPublic ? "隐藏" : "公开" }}
                          </button>
                          <span v-if="item.pointsCost" class="studio-points-cost">{{ formatPointsCost(item.pointsCost) }}</span>
                        </div>
                      </div>
                  </article>
                </div>

                <div v-else-if="currentVideoTask && (currentVideoTask.status === 'failed' || currentVideoTask.status === 'canceled')" class="studio-history-empty studio-history-empty-inline">
                  {{ currentVideoTask.errorMessage || "视频生成失败，请重新尝试。" }}
                </div>

                <div v-else class="studio-history-empty studio-history-empty-inline">
                  输入提示词后开始生成视频。
                </div>
                <div ref="videoBottom" class="studio-scroll-anchor"></div>
              </div>

              <div class="studio-composer studio-composer-inline">
                <textarea
                  v-model="draft"
                  class="studio-textarea"
                  :placeholder="currentConfig.placeholder"
                ></textarea>
                <div class="studio-video-reference-block">
                  <div class="studio-video-reference-head">
                    <strong>参考图片</strong>
                    <span>最多 2 张，可上传图片或从图片历史选择</span>
                  </div>
                  <div class="studio-video-reference-grid">
                    <div
                      v-for="(item, index) in videoImageSlots"
                      :key="'video-image-slot-' + index"
                      class="studio-video-reference-card"
                    >
                      <div class="studio-video-reference-preview">
                        <img
                          v-if="item.url"
                          :src="item.url"
                          :alt="'参考图片 ' + (index + 1)"
                          class="studio-video-reference-image"
                        >
                        <span v-else>{{ index === 0 ? "首帧参考图" : "尾帧参考图" }}</span>
                      </div>
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        class="studio-reference-file-input"
                        @change="handleVideoReferenceFileChange(index, $event)"
                      >
                      <div class="studio-video-reference-actions">
                        <button
                          type="button"
                          class="ghost-button studio-reference-button"
                          @click="openVideoReferencePicker(index)"
                        >
                          从图片历史选择
                        </button>
                        <span v-if="videoImageUploading[index]" class="studio-reference-uploading">上传中...</span>
                        <button
                          v-if="item.url"
                          type="button"
                          class="ghost-button studio-reference-button studio-reference-button-muted"
                          @click="clearVideoImageUrl(index)"
                        >
                          清空
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="studio-inline-fields studio-inline-fields-video">
                  <select v-model.number="videoDuration" class="studio-inline-select">
                    <option v-for="duration in videoDurations" :key="duration" :value="duration">{{ duration }} 秒</option>
                  </select>
                  <select v-model="videoRatio" class="studio-inline-select">
                    <option v-for="ratio in videoRatios" :key="ratio" :value="ratio">{{ ratio }}</option>
                  </select>
                  <select v-model="videoResolution" class="studio-inline-select">
                    <option v-for="resolution in videoResolutions" :key="resolution" :value="resolution">{{ resolution }}</option>
                  </select>
                  <select v-model="videoModal" class="studio-inline-select">
                    <option v-for="modal in videoModals" :key="modal" :value="modal">{{ modal }}</option>
                  </select>
                </div>
                <label class="studio-switch-row">
                  <input v-model="videoWatermark" type="checkbox">
                  <span>添加平台水印</span>
                </label>
                <div class="studio-composer-bar">
                  <div class="studio-composer-meta">
                    <span>{{ videoHint }}</span>
                    <small>{{ videoPointCostText }}</small>
                  </div>
                  <div class="studio-composer-actions">
                    <button
                      type="button"
                      class="studio-submit"
                      :disabled="generatingVideo || videoReferenceUploading || !trimmedDraft"
                      @click="handleGenerateVideo"
                    >
                      {{ generatingVideo ? "生成中..." : currentConfig.actionLabel }}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section v-else-if="mode === 'speech'" class="studio-panel studio-panel-main studio-panel-picture">
              <div class="studio-picture-body">
                <div v-if="speechStatus" class="studio-status-text">{{ speechStatus }}</div>

                <div class="studio-gallery-header">
                  <div>
                    <p class="studio-eyebrow">Voice Studio</p>
                    <h3>语音生成结果</h3>
                  </div>
                  <span class="studio-meta-note">{{ speechMetaNote }}</span>
                </div>

                <div v-if="speechLoadingDetail" class="studio-history-empty studio-history-empty-inline">
                  正在加载语音详情...
                </div>

                <article v-else-if="currentSpeechTask" class="studio-speech-card">
                  <div class="studio-speech-player">
                    <audio
                      v-if="currentSpeechTask.audioUrl"
                      class="studio-speech-audio"
                      :src="currentSpeechTask.audioUrl"
                      controls
                      preload="metadata"
                    ></audio>
                    <div v-else class="studio-history-empty studio-history-empty-inline">
                      语音生成结果暂未返回，请稍后刷新查看。
                    </div>
                  </div>
                  <div class="studio-video-copy">
                    <div class="studio-video-title-row">
                      <strong>{{ currentSpeechTask.title }}</strong>
                      <span v-if="currentSpeechTask.pointsCost">{{ formatPointsCost(currentSpeechTask.pointsCost) }}</span>
                    </div>
                    <p>{{ currentSpeechTask.text }}</p>
                  </div>
                </article>

                <div v-else class="studio-history-empty studio-history-empty-inline">
                  输入文案后开始生成语音。
                </div>
              </div>

              <div class="studio-composer studio-composer-inline">
                <textarea
                  v-model="draft"
                  class="studio-textarea"
                  :placeholder="currentConfig.placeholder"
                ></textarea>
                <div class="studio-inline-fields studio-inline-fields-video">
                  <select v-model="speechVoiceType" class="studio-inline-select">
                    <option value="zh_female_tianmeisongai_moon_bigtts">甜美女声</option>
                  </select>
                  <select v-model="speechEncoding" class="studio-inline-select">
                    <option value="mp3">MP3</option>
                  </select>
                  <select v-model.number="speechSpeedRatio" class="studio-inline-select">
                    <option :value="0.8">慢速 0.8x</option>
                    <option :value="1">标准 1x</option>
                    <option :value="1.2">快速 1.2x</option>
                  </select>
                </div>
                <div class="studio-inline-fields studio-inline-fields-video">
                  <select v-model.number="speechVolumeRatio" class="studio-inline-select">
                    <option :value="0.8">音量 0.8</option>
                    <option :value="1">音量 1</option>
                    <option :value="1.2">音量 1.2</option>
                  </select>
                  <select v-model.number="speechPitchRatio" class="studio-inline-select">
                    <option :value="0.8">音调 0.8</option>
                    <option :value="1">音调 1</option>
                    <option :value="1.2">音调 1.2</option>
                  </select>
                </div>
                <div class="studio-composer-bar">
                  <div class="studio-composer-meta">
                    <span>{{ speechHint }}</span>
                    <small>{{ scenePointCostText("ai_speech") }}</small>
                  </div>
                  <div class="studio-composer-actions">
                    <button
                      type="button"
                      class="studio-submit"
                      :disabled="generatingSpeech || !trimmedDraft"
                      @click="handleGenerateSpeech"
                    >
                      {{ generatingSpeech ? "生成中..." : currentConfig.actionLabel }}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <div
        v-if="videoCostDialog.visible"
        class="studio-lightbox"
      >
        <div class="studio-confirm-panel">
          <strong>确认生成视频</strong>
          <p>本次生成需要消耗{{ videoCostDialog.cost }}积分，生成完成后剩余积分{{ videoCostDialog.remaining }}</p>
          <div class="studio-confirm-actions">
            <button
              type="button"
              class="ghost-button"
              @click="resolveVideoCostConfirm(false)"
            >
              取消
            </button>
            <button
              type="button"
              class="studio-submit"
              @click="resolveVideoCostConfirm(true)"
            >
              确认
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="voiceListening"
        class="studio-lightbox studio-voice-listening-layer"
      >
        <div class="studio-lightbox-panel studio-voice-listening-panel">
          <div class="studio-voice-pulse" aria-hidden="true">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <div class="studio-voice-listening-copy">
            <strong>正在收音</strong>
            <p>请继续说话，语音输入结束后会自动关闭。</p>
          </div>
          <button
            type="button"
            class="ghost-button studio-voice-stop-button"
            @click.stop="stopVoiceInput('modal-button')"
          >
            结束收音
          </button>
        </div>
      </div>

      <div
        v-if="previewImage"
        class="studio-lightbox"
        @click.self="closeImagePreview"
      >
        <button
          type="button"
          class="studio-lightbox-close"
          @click="closeImagePreview"
        >
          Close
        </button>
        <div class="studio-lightbox-panel">
          <div class="studio-lightbox-media">
            <img
              class="studio-lightbox-image"
              :src="previewImage.src"
              :alt="previewImage.title"
            >
          </div>
          <div class="studio-lightbox-copy">
            <strong>{{ previewImage.title }}</strong>
            <span>{{ previewImage.tag }}</span>
            <p>{{ previewImage.prompt }}</p>
          </div>
        </div>
      </div>

      <div
        v-if="imageReferencePicker.visible"
        class="studio-lightbox"
        @click.self="closeImageReferencePicker"
      >
        <div class="studio-lightbox-panel studio-reference-picker">
          <button
            type="button"
            class="studio-lightbox-close"
            @click="closeImageReferencePicker"
          >
            Close
          </button>

          <div class="studio-reference-picker-copy">
            <strong>选择参考图片</strong>
            <span>可从历史图片作品中选 1 张作为当前生成参考图。</span>
          </div>

          <div v-if="imageReferenceLoading" class="studio-history-empty studio-history-empty-inline">
            正在加载图片历史...
          </div>

          <div v-else-if="!imageReferenceCandidates.length" class="studio-history-empty studio-history-empty-inline">
            暂无可选图片，请先去图片模块生成作品，或直接填写公网图片 URL。
          </div>

          <div v-else class="studio-reference-picker-grid">
            <button
              v-for="item in imageReferenceCandidates"
              :key="item.id"
              type="button"
              class="studio-reference-picker-item"
              @click="selectImageReferenceImage(item)"
            >
              <div class="studio-reference-picker-frame">
                <img :src="item.src" :alt="item.title" class="studio-reference-picker-image">
              </div>
              <div class="studio-reference-picker-meta">
                <strong>{{ item.title }}</strong>
                <span>{{ item.taskTitle }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="videoReferencePicker.visible"
        class="studio-lightbox"
        @click.self="closeVideoReferencePicker"
      >
        <div class="studio-lightbox-panel studio-reference-picker">
          <button
            type="button"
            class="studio-lightbox-close"
            @click="closeVideoReferencePicker"
          >
            Close
          </button>

          <div class="studio-reference-picker-copy">
            <strong>选择参考图片</strong>
            <span>最多使用两张已生成图片，作为视频首帧或尾帧参考。</span>
          </div>

          <div v-if="videoReferenceLoading" class="studio-history-empty studio-history-empty-inline">
            正在加载图片历史...
          </div>

          <div v-else-if="!videoReferenceCandidates.length" class="studio-history-empty studio-history-empty-inline">
            暂无可选图片，请先去图片模块生成作品，或直接填写公网图片 URL。
          </div>

          <div v-else class="studio-reference-picker-grid">
            <button
              v-for="item in videoReferenceCandidates"
              :key="item.id"
              type="button"
              class="studio-reference-picker-item"
              @click="selectVideoReferenceImage(item)"
            >
              <div class="studio-reference-picker-frame">
                <img :src="item.src" :alt="item.title" class="studio-reference-picker-image">
              </div>
              <div class="studio-reference-picker-meta">
                <strong>{{ item.title }}</strong>
                <span>{{ item.taskTitle }}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSidebar from "../components/AppSidebar.vue";
import { getCurrentUser, refreshToken } from "../api/auth";
import {
  createAiImage,
  createAiSpeech,
  createAiVideo,
  createAiConversation,
  deleteAiConversation,
  deleteAiImage,
  deleteAiSpeech,
  deleteAiVideo,
  getAiImageDetail,
  getAiImages,
  getAiSpeechDetail,
  getAiSpeeches,
  getAiPointCosts,
  getAiVideoDetail,
  getAiVideos,
  getAiConversationDetail,
  getAiConversations,
  streamAiConversationMessage,
  uploadAiImageFile,
  updateAiImageVisibility,
  updateAiVideoVisibility
} from "../api/ai";
import { clearAuthState, readAuthState, saveAuthState } from "../utils/auth";

var STATIC_CONFIGS = {
  picture: {
    brand: "Image Lab",
    title: "图片创作台",
    shortTitle: "图片项目",
    subtitle: "已接入真实文生图接口，支持历史任务、恢复详情和多图结果展示。",
    header: "AI 图片生成",
    actionLabel: "生成图片",
    placeholder: "输入画面描述，例如：霓虹城市、兔灵智绘少女、电影感构图、蓝橙对比光...",
    footerHint: "建议包含主体、风格、镜头、光线和比例",
    metaNote: "等待生成",
    chips: ["未来城市夜景", "兔灵智绘少女海报", "AI 教室概念图", "未来展厅 KV"]
  },
  video: {
    brand: "Video Lab",
    title: "视频创作台",
    shortTitle: "视频项目",
    subtitle: "已接入真实文生视频接口，支持历史任务、异步轮询和结果预览。",
    header: "AI 视频生成",
    actionLabel: "生成视频",
    placeholder: "输入视频镜头描述，例如：镜头缓慢推进，人物回头，霓虹城市灯光流动...",
    footerHint: "建议描述动作、镜头运动、时长和场景氛围",
    metaNote: "等待生成",
    chips: ["未来城市飞车", "课堂开场动画", "人物慢镜头转场", "电影感霓虹长镜头"]
  },
  speech: {
    brand: "Voice Lab",
    title: "语音创作台",
    shortTitle: "语音项目",
    subtitle: "将文本快速转换为自然语音，支持历史记录和音频播放。",
    header: "AI 语音生成",
    actionLabel: "生成语音",
    placeholder: "输入要转换成语音的文字，例如：欢迎来到兔灵智绘，我们将一起学习人工智能创作。",
    footerHint: "建议输入完整文案，生成后可直接在线播放",
    metaNote: "等待生成",
    chips: ["欢迎来到兔灵智绘", "课程开场旁白", "作品展示解说", "活动通知播报"]
  }
};

var IMAGE_STYLE_PRESETS = [
  {
    id: "2d-cartoon",
    name: "2D卡通风",
    image: "/image/styles/2d-cartoon.jpeg",
    prompt: "2D 卡通，日系动漫风格，干净流畅的轮廓线条，平涂上色，色块分明，柔和简化阴影，高饱和明快色彩，画面整洁无多余杂线，完整构图，高清卡通插画质感，线条清晰锐利"
  },
  {
    id: "3d-cartoon",
    name: "3D卡通风",
    image: "/image/styles/3d-cartoon.jpeg",
    prompt: "3D 卡通渲染，动画电影质感，圆润柔和建模，细腻哑光材质，明亮全局光照，明快饱满色彩，干净光滑表面，自然柔和阴影，8K 高清，毛发衣料细节丰富，电影级打光，浅景深，完整构图"
  },
  {
    id: "q-version",
    name: "Q版风",
    image: "/image/styles/q-version.jpeg",
    prompt: "2D Q 版卡通，圆润可爱造型，简洁勾线，平涂填色，马卡龙柔和配色，软萌治愈风格，无尖锐棱角，造型夸张讨喜，明亮均匀柔光，干净画面，完整构图"
  },
  {
    id: "flat-vector",
    name: "扁平矢量风",
    image: "/image/styles/flat-vector.jpeg",
    prompt: "2D 扁平卡通，极简风格，纯色色块无渐变，简洁线条，扁平化设计，低细节度，干净清爽，几何感造型，矢量插画质感，统一配色，画面整洁"
  },
  {
    id: "ink-wash",
    name: "水墨写意风",
    image: "/image/styles/ink-wash.jpeg",
    prompt: "中国水墨写意画，国风山水，宣纸质感，墨色浓淡干湿变化，毛笔笔触，大面积留白，云雾缭绕，意境悠远，淡雅黑白灰为主，点缀少量赭石花青，东方美学，2D 平面艺术"
  },
  {
    id: "cyberpunk",
    name: "赛博朋克风",
    image: "/image/styles/cyberpunk.jpeg",
    prompt: "赛博朋克风格，单人角色，机能风服饰，义体改造细节，霓虹光效打在面部与衣物上，未来感配饰，背景是虚化的雨夜赛博都市，全息广告光斑，蓝紫冷色调为主，高对比度光影，金属与皮革质感，精致二次元画风，8K 高清，细节丰富，景深效果，电影感氛围"
  },
  {
    id: "xianxia",
    name: "古风仙侠风",
    image: "/image/styles/xianxia.jpeg",
    prompt: "中国古风仙侠风格，2D 插画，飘逸衣袂，云雾仙气化境，金线条纹点缀，雅致低饱和配色，柔和丁达尔柔光，仙山亭台背景，东方古典仙气，细腻发丝衣料，氛围感拉满"
  },
  {
    id: "designer-toy",
    name: "3D潮玩手办风",
    image: "/image/styles/designer-toy.jpeg",
    prompt: "3D 潮玩手办，哑光 PVC 材质，精致漆面质感，影棚柔和打光，纯色简洁背景，立体饱满造型，细节精致，均匀柔光，高品质渲染，8K 超清，手办摆件质感，干净无瑕疵"
  },
  {
    id: "realistic",
    name: "写实风",
    image: "/image/styles/realistic.jpeg",
    prompt: "超写实风格，专业摄影质感，真实物理光影，材质细节精准还原，画面锐利清晰，8K 超清分辨率，自然环境光，真实景深效果，高细节纹理，色彩还原自然，构图完整，细腻真实的肌理表现"
  }
];

export default {
  name: "ChatStudioView",
  components: {
    AppSidebar: AppSidebar
  },
  data: function data() {
    return {
      auth: readAuthState(),
      draft: "",
      textHistory: [],
      textMessages: [],
      imageHistory: [],
      videoHistory: [],
      speechHistory: [],
      currentImageTask: null,
      currentVideoTask: null,
      currentSpeechTask: null,
      currentConversation: null,
      loadingHistory: false,
      loadingImageHistory: false,
      loadingVideoHistory: false,
      loadingSpeechHistory: false,
      loadingConversation: false,
      imageLoadingDetail: false,
      videoLoadingDetail: false,
      speechLoadingDetail: false,
      creatingConversation: false,
      sendingMessage: false,
      generatingImage: false,
        generatingVideo: false,
        generatingSpeech: false,
        textStatus: "",
        imageStatus: "",
        videoStatus: "",
        speechStatus: "",
        streamingMessageId: null,
        previewImage: null,
        deletingHistoryId: null,
        imageNegativePrompt: "模糊，低质量，水印，畸形人物",
      imageSize: "1:1",
      imageCount: 1,
      selectedImageStyleId: "",
      currentVideoProjectId: null,
      videoDuration: 5,
      videoRatio: "16:9",
      videoResolution: "720p",
      videoModal: "Doubao-Seedance-2.0-fast",
      videoWatermark: false,
      videoPollTimer: null,
      videoPlaybackErrors: {},
      speechRecognition: null,
      voiceListening: false,
      voiceDraftBase: "",
      voiceFinalTranscript: "",
      voiceStartedAt: 0,
      autoScrolledKeys: {},
      studioSidebarExpanded: {
        text: false,
        picture: false,
        video: false,
        speech: false
      },
      imageReferenceUrls: [""],
      imageReferenceUploading: [false],
      imageReferenceLoading: false,
      imageReferenceCandidates: [],
      imageReferencePicker: {
        visible: false,
        slotIndex: 0
      },
      videoImageUrls: ["", ""],
      videoImageUploading: [false, false],
      videoReferenceLoading: false,
      videoReferenceCandidates: [],
      videoReferencePicker: {
        visible: false,
        slotIndex: 0
      },
      pointCostMap: {},
      pointCostsLoading: false,
      pointCostsLoaded: false,
      videoCostDialog: {
        visible: false,
        cost: 0,
        balance: 0,
        remaining: 0
      },
      pendingVideoCostConfirm: null,
      speechVoiceType: "zh_female_tianmeisongai_moon_bigtts",
      speechEncoding: "mp3",
      speechSpeedRatio: 1,
      speechVolumeRatio: 1,
      speechPitchRatio: 1
    };
  },
  computed: {
    mode: function mode() {
      return this.$route.meta.type || "text";
    },
    isStudioSidebarExpanded: function isStudioSidebarExpanded() {
      return this.studioSidebarExpanded[this.mode] !== false;
    },
    currentConfig: function currentConfig() {
      if (this.mode === "text") {
        return {
          brand: "Text Lab",
          title: "文本创作台",
          shortTitle: "文本会话",
          subtitle: "已接入真实文本会话接口，支持历史会话、恢复上下文和连续追问。",
          header: "AI 文本对话",
          actionLabel: "发送消息",
          placeholder: "输入你的问题，例如：请帮我生成一个活动页标题",
          footerHint: "系统会自动带上当前会话历史，不需要重复上传上下文",
          chips: ["写一个活动标题", "扩写成宣传文案", "整理成提纲", "改成更有科技感"],
          history: []
        };
      }

      return STATIC_CONFIGS[this.mode];
    },
    sidebarHistory: function sidebarHistory() {
      if (this.mode === "text") {
        return this.textHistory;
      }

      if (this.mode === "picture") {
        return this.imageHistory;
      }

      if (this.mode === "video") {
        return this.videoHistory;
      }

      if (this.mode === "speech") {
        return this.speechHistory;
      }

      return this.currentConfig.history || [];
    },
    primaryActionLabel: function primaryActionLabel() {
      if (this.mode === "text" && this.creatingConversation) {
        return "创建中...";
      }

      if (this.mode === "picture" && this.generatingImage) {
        return "生成中...";
      }

      if (this.mode === "video" && this.generatingVideo) {
        return "生成中...";
      }

      if (this.mode === "speech" && this.generatingSpeech) {
        return "生成中...";
      }

      return "新建" + this.currentConfig.shortTitle;
    },
    trimmedDraft: function trimmedDraft() {
      return (this.draft || "").trim();
    },
    currentConversationHint: function currentConversationHint() {
      if (this.loadingConversation) {
        return "正在恢复当前会话...";
      }

      if (this.currentConversation && this.currentConversation.title) {
        return "当前会话：" + this.currentConversation.title;
      }

      return this.currentConfig.footerHint;
    },
    currentConversationId: function currentConversationId() {
      if (this.mode !== "text") {
        return "";
      }

      if (this.$route.params.id) {
        return String(this.$route.params.id);
      }

      if (this.currentConversation && this.currentConversation.id) {
        return String(this.currentConversation.id);
      }

      return "";
      },
      currentImageAssets: function currentImageAssets() {
        return normalizeImageAssets(this.currentImageTask);
      },
    pictureMetaNote: function pictureMetaNote() {
      if (this.imageLoadingDetail) {
        return "加载中";
      }

      if (!this.currentImageTask) {
        return this.currentConfig.metaNote;
      }

      if (this.currentImageTask.statusText) {
        return this.currentImageTask.statusText;
      }

      return "已生成 " + this.currentImageAssets.length + " 张";
    },
    pictureHint: function pictureHint() {
      if (this.generatingImage) {
        return "图片任务已提交，正在生成...";
      }

      if (this.currentImageTask && this.currentImageTask.prompt) {
        return "当前任务：" + this.currentImageTask.prompt;
      }

      return this.currentConfig.footerHint;
      },
      currentVideoMessages: function currentVideoMessages() {
        return normalizeVideoMessages(this.currentVideoTask);
      },
    videoMetaNote: function videoMetaNote() {
      if (this.videoLoadingDetail) {
        return "加载中";
      }

      if (!this.currentVideoTask) {
        return this.currentConfig.metaNote;
      }

      return this.currentVideoTask.statusText || this.currentConfig.metaNote;
    },
    videoHint: function videoHint() {
      if (this.generatingVideo) {
        return "视频任务已提交，正在生成...";
      }

      if (this.currentVideoTask && this.currentVideoTask.prompt) {
        return "当前任务：" + this.currentVideoTask.prompt;
      }

      return this.currentConfig.footerHint;
    },
    videoPointCost: function videoPointCost() {
      return normalizePointsCost(this.pointCostMap.ai_video);
    },
    videoPointCostText: function videoPointCostText() {
      if (this.pointCostsLoading && !this.videoPointCost) {
        return "正在加载积分消耗...";
      }

      return this.videoPointCost ? "本次消耗积分：" + this.videoPointCost : "本次消耗积分以生成结果为准";
    },
    currentPointsBalance: function currentPointsBalance() {
      var user = this.auth && this.auth.user ? this.auth.user : {};

      return normalizePointsCost(user.points_balance || user.pointsBalance || 0);
    },
    speechMetaNote: function speechMetaNote() {
      if (this.speechLoadingDetail) {
        return "加载中";
      }

      if (!this.currentSpeechTask) {
        return this.currentConfig.metaNote;
      }

      return this.currentSpeechTask.audioUrl ? "已生成" : this.currentConfig.metaNote;
    },
    speechHint: function speechHint() {
      if (this.generatingSpeech) {
        return "语音正在生成...";
      }

      if (this.currentSpeechTask && this.currentSpeechTask.text) {
        return "当前任务：" + this.currentSpeechTask.text;
      }

      return this.currentConfig.footerHint;
    },
    imageSizeOptions: function imageSizeOptions() {
      return [
        { label: "正方形 1:1", value: "1:1" },
        { label: "横版 16:9", value: "16:9" },
        { label: "竖版 9:16", value: "9:16" },
        { label: "横版 720p", value: "720p" },
        { label: "横版 1080p", value: "1080p" },
        { label: "4:3", value: "4:3" },
        { label: "3:4", value: "3:4" },
        { label: "21:9", value: "21:9" }
      ];
    },
    imageStyleOptions: function imageStyleOptions() {
      return IMAGE_STYLE_PRESETS.map(function(style) {
        return {
          id: style.id,
          name: style.name,
          image: style.image
        };
      });
    },
    imageReferenceSlots: function imageReferenceSlots() {
      return this.imageReferenceUrls.map(function(url, index) {
        return {
          index: index,
          url: String(url || "").trim()
        };
      });
    },
    imageReferenceUploadingState: function imageReferenceUploadingState() {
      return this.imageReferenceUploading.some(Boolean);
    },
    videoDurations: function videoDurations() {
      return [5, 6, 7, 8, 9, 10];
    },
    videoRatios: function videoRatios() {
      return ["16:9", "9:16", "1:1"];
    },
    videoResolutions: function videoResolutions() {
      return ["480p", "720p", "1080p"];
    },
    videoModals: function videoModals() {
      return [
        "Doubao-Seedance-2.0",
        "Doubao-Seedance-2.0-fast"
      ];
    },
    videoImageSlots: function videoImageSlots() {
      return this.videoImageUrls.map(function(url, index) {
        return {
          index: index,
          url: String(url || "").trim()
        };
      });
    },
    videoReferenceUploading: function videoReferenceUploading() {
      return this.videoImageUploading.some(Boolean);
    },
    voiceButtonLabel: function voiceButtonLabel() {
      return this.voiceListening ? "停止听写" : "语音输入";
    }
  },
  created: function created() {
    this.bootstrapMode();
  },
  mounted: function mounted() {
    this.initializeVoiceInput();
  },
  beforeDestroy: function beforeDestroy() {
    this.stopVideoPolling();
    this.stopVoiceInput("before-destroy");
  },
  watch: {
    mode: function modeChanged() {
      this.stopVideoPolling();
      this.bootstrapMode();
    },
    "$route.params.id": function routeIdChanged() {
      if (this.mode === "text") {
        this.bootstrapTextMode();
        return;
      }

      if (this.mode === "picture") {
        this.bootstrapPictureMode();
        return;
      }

      if (this.mode === "video") {
        if (this.generatingVideo) {
          return;
        }

        this.bootstrapVideoMode();
        return;
      }

      if (this.mode === "speech") {
        this.bootstrapSpeechMode();
      }
    },
    "$route.query.new": function routeQueryChanged() {
      if (this.mode === "picture") {
        this.bootstrapPictureMode();
        return;
      }

      if (this.mode === "video") {
        if (this.generatingVideo) {
          return;
        }

        this.bootstrapVideoMode();
        return;
      }

      if (this.mode === "speech") {
        this.bootstrapSpeechMode();
      }
    }
  },
  methods: {
    initializeVoiceInput: function initializeVoiceInput() {
      if (typeof window === "undefined") {
        return;
      }

      var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

      if (!SpeechRecognition) {
        return;
      }
    },
    toggleVoiceInput: function toggleVoiceInput() {
      this.debugVoiceLog("toggle", {
        listening: this.voiceListening,
        mode: this.mode
      });

      if (this.voiceListening) {
        this.stopVoiceInput("toggle-button");
        return;
      }

      this.startVoiceInput();
    },
    startVoiceInput: function startVoiceInput() {
      this.debugVoiceLog("start requested", {
        mode: this.mode,
        hasWindow: typeof window !== "undefined"
      });

      if (typeof window === "undefined") {
        return;
      }

      var SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;

      if (!SpeechRecognition) {
        this.debugVoiceLog("unsupported", {
          SpeechRecognition: !!window.SpeechRecognition,
          webkitSpeechRecognition: !!window.webkitSpeechRecognition
        });
        this.setVoiceStatus("当前浏览器暂不支持语音输入。");
        return;
      }

      this.voiceDraftBase = this.trimmedDraft ? this.trimmedDraft + " " : this.draft;
      this.voiceFinalTranscript = "";
      this.voiceStartedAt = Date.now();

      var recognition = new SpeechRecognition();
      var self = this;

      this.debugVoiceLog("recognition created", {
        baseLength: this.voiceDraftBase.length
      });

      recognition.lang = "zh-CN";

      recognition.onstart = function() {
        self.debugVoiceLog("onstart", {
          listening: self.voiceListening
        });

        self.voiceListening = true;
      };

      recognition.onresult = function(event) {
        self.debugVoiceLog("onresult", {
          resultIndex: event.resultIndex,
          resultsLength: event.results ? event.results.length : 0
        });

        var result = event.results && event.results[0] && event.results[0][0]
          ? (event.results[0][0].transcript || "")
          : "";
        self.voiceFinalTranscript = result;
        self.draft = mergeVoiceDraft(self.voiceDraftBase, result);
        self.debugVoiceLog("draft updated", {
          finalTranscript: result,
          interimTranscript: "",
          draftLength: self.draft.length
        });
      };

      recognition.onerror = function(event) {
        self.debugVoiceLog("onerror", {
          error: event && event.error
        });

        self.voiceListening = false;
        self.speechRecognition = null;

        if (event && event.error === "not-allowed") {
          self.setVoiceStatus("语音输入权限被拒绝，请检查浏览器麦克风权限。");
          return;
        }

        if (event && event.error === "no-speech") {
          self.setVoiceStatus("没有检测到语音，请再试一次。");
          return;
        }

        self.setVoiceStatus("语音识别失败，请稍后重试。");
      };

      recognition.onend = function() {
        self.debugVoiceLog("onend", {
          finalTranscript: self.voiceFinalTranscript,
          draftLength: self.draft.length
        });

        self.voiceListening = false;
        self.speechRecognition = null;
      };

      this.speechRecognition = recognition;

      try {
        this.debugVoiceLog("calling recognition.start", {
          mode: this.mode
        });
        recognition.start();
        this.voiceListening = true;
      } catch (error) {
        this.debugVoiceLog("start threw", {
          message: error && error.message,
          name: error && error.name
        });
        this.voiceListening = false;
        this.speechRecognition = null;
        this.setVoiceStatus("语音输入暂时无法启动，请稍后重试。");
      }
    },
    stopVoiceInput: function stopVoiceInput(source, options) {
      var recognition = this.speechRecognition;

      this.debugVoiceLog("stop requested", {
        source: source || "unknown",
        hasRecognition: !!recognition,
        listening: this.voiceListening
      });

      this.voiceListening = false;
      this.speechRecognition = null;
      this.voiceStartedAt = 0;

      if (!recognition) {
        this.debugVoiceLog("stop skipped no recognition", {
          listening: this.voiceListening
        });
        return;
      }

      try {
        this.debugVoiceLog("calling recognition.stop", {
          source: source || "unknown"
        });
        recognition.stop();
      } catch (error) {
        this.debugVoiceLog("stop threw, trying abort", {
          message: error && error.message,
          name: error && error.name
        });
        try {
          recognition.abort();
        } catch (abortError) {
          this.debugVoiceLog("abort threw", {
            message: abortError && abortError.message,
            name: abortError && abortError.name
          });
          // Ignore browser stop errors.
        }
      }
    },
    debugVoiceLog: function debugVoiceLog(eventName, payload) {
      if (typeof console === "undefined") {
        return;
      }

      console.log("[voice-debug] " + eventName, payload || {});
    },
    toggleStudioSidebar: function toggleStudioSidebar() {
      this.$set(this.studioSidebarExpanded, this.mode, !this.isStudioSidebarExpanded);
    },
    setVoiceStatus: function setVoiceStatus(message) {
      if (this.mode === "text") {
        this.textStatus = message;
        return;
      }

      if (this.mode === "picture") {
        this.imageStatus = message;
        return;
      }

      if (this.mode === "video") {
        this.videoStatus = message;
        return;
      }

      if (this.mode === "speech") {
        this.speechStatus = message;
      }
    },
    async bootstrapMode() {
      this.auth = readAuthState();

      if (this.auth.accessToken) {
        await this.ensurePointCosts();
      }

      if (this.mode === "text") {
        await this.bootstrapTextMode();
        return;
      }

      if (this.mode === "picture") {
        await this.bootstrapPictureMode();
        return;
      }

      if (this.mode === "video") {
        await this.bootstrapVideoMode();
        return;
      }

      if (this.mode === "speech") {
        await this.bootstrapSpeechMode();
        return;
      }

      this.draft = "";
      this.textStatus = "";
    },
    async bootstrapTextMode() {
      this.auth = readAuthState();
      this.draft = "";

      if (!this.auth.accessToken) {
        this.handleUnauthorized();
        return;
      }

      try {
        await this.runWithAuthRetry(async () => {
          await this.fetchTextHistory();

          if (this.$route.params.id) {
            await this.fetchConversationDetail(this.$route.params.id);
            return;
          }

          if (this.textHistory.length) {
            this.$router.replace({
              name: "chat-text",
              params: {
                id: String(this.textHistory[0].id)
              }
            });
            return;
          }

          this.currentConversation = null;
          this.textMessages = [];
          this.textStatus = "";
        });
      } catch (error) {
        this.textStatus = error.message || "文本会话加载失败，请稍后重试。";
      }
    },
    async bootstrapPictureMode() {
      this.stopVideoPolling();
      this.auth = readAuthState();
      this.draft = "";
      this.imageStatus = "";

      if (!this.auth.accessToken) {
        this.handleUnauthorized();
        return;
      }

      try {
        await this.runWithAuthRetry(async () => {
          await this.fetchImageHistory();

          if (this.$route.query.new === "1") {
            this.currentImageTask = null;
            this.imageReferenceUrls = [""];
            return;
          }

          if (this.$route.params.id) {
            await this.fetchImageDetail(this.$route.params.id);
            return;
          }

          if (this.imageHistory.length) {
            this.$router.replace({
              name: "chat-picture",
              params: {
                id: String(this.imageHistory[0].id)
              }
            });
            return;
          }

          this.currentImageTask = null;
          this.imageReferenceUrls = [""];
        });
      } catch (error) {
        this.imageStatus = error.message || "图片任务加载失败，请稍后重试。";
      }
    },
    async bootstrapVideoMode() {
      this.stopVideoPolling();
      this.auth = readAuthState();
      this.draft = "";
      this.videoStatus = "";

      if (!this.auth.accessToken) {
        this.handleUnauthorized();
        return;
      }

      try {
        await this.runWithAuthRetry(async () => {
          await this.fetchVideoHistory();

          if (this.$route.query.new === "1") {
            this.currentVideoTask = null;
            this.currentVideoProjectId = null;
            this.videoImageUrls = ["", ""];
            return;
          }

          if (this.$route.params.id) {
            await this.fetchVideoDetail(this.$route.params.id, { refresh: false });
            this.resumeVideoPollingIfNeeded(this.$route.params.id);
            return;
          }

          if (this.videoHistory.length) {
            var nextVideoId = String(this.videoHistory[0].projectId || this.videoHistory[0].id);

            await this.$router.replace({
              name: "chat-video",
              params: {
                id: nextVideoId
              }
            });
            await this.fetchVideoDetail(nextVideoId, { refresh: false });
            this.resumeVideoPollingIfNeeded(nextVideoId);
            return;
          }

          this.currentVideoTask = null;
          this.currentVideoProjectId = null;
          this.videoImageUrls = ["", ""];
        });
      } catch (error) {
        this.videoStatus = error.message || "视频任务加载失败，请稍后重试。";
      }
    },
    async bootstrapSpeechMode() {
      this.stopVideoPolling();
      this.auth = readAuthState();
      this.draft = "";
      this.speechStatus = "";

      if (!this.auth.accessToken) {
        this.handleUnauthorized();
        return;
      }

      try {
        await this.runWithAuthRetry(async () => {
          await this.fetchSpeechHistory();

          if (this.$route.query.new === "1") {
            this.currentSpeechTask = null;
            return;
          }

          if (this.$route.params.id) {
            await this.fetchSpeechDetail(this.$route.params.id);
            return;
          }

          if (this.speechHistory.length) {
            this.$router.replace({
              name: "chat-speech",
              params: {
                id: String(this.speechHistory[0].id)
              }
            });
            return;
          }

          this.currentSpeechTask = null;
        });
      } catch (error) {
        this.speechStatus = error.message || "语音任务加载失败，请稍后重试。";
      }
    },
    async fetchTextHistory() {
      this.loadingHistory = true;

      try {
        var payload = await getAiConversations(this.auth.accessToken);
        var source = unwrapPayload(payload);
        var list = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : []);
        this.textHistory = list.map(normalizeConversationSummary);
      } finally {
        this.loadingHistory = false;
      }
    },
    async fetchImageHistory() {
      this.loadingImageHistory = true;

      try {
        var payload = await getAiImages(this.auth.accessToken);
        var source = unwrapPayload(payload);
        var list = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : []);
        this.imageHistory = list.map(normalizeImageSummary);
      } finally {
        this.loadingImageHistory = false;
      }
    },
    async fetchVideoHistory() {
      this.loadingVideoHistory = true;

      try {
        var payload = await getAiVideos(this.auth.accessToken);
        var source = unwrapPayload(payload);
        var list = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : []);
        this.videoHistory = list.map(normalizeVideoSummary);
      } finally {
        this.loadingVideoHistory = false;
      }
    },
    async fetchSpeechHistory() {
      this.loadingSpeechHistory = true;

      try {
        var payload = await getAiSpeeches(this.auth.accessToken);
        var source = unwrapPayload(payload);
        var list = Array.isArray(source) ? source : (Array.isArray(source.data) ? source.data : []);
        this.speechHistory = list.map(normalizeSpeechSummary);
      } finally {
        this.loadingSpeechHistory = false;
      }
    },
    async ensurePointCosts() {
      if (this.pointCostsLoaded || this.pointCostsLoading || !this.auth.accessToken) {
        return;
      }

      this.pointCostsLoading = true;

      try {
        var payload = await getAiPointCosts(this.auth.accessToken);
        var source = unwrapPayload(payload);
        this.pointCostMap = normalizePointCostMap(source);
        this.pointCostsLoaded = true;
      } catch (error) {
        this.pointCostsLoaded = false;
      } finally {
        this.pointCostsLoading = false;
      }
    },
    async refreshCurrentUser() {
      if (!this.auth.accessToken) {
        return;
      }

      var user = await getCurrentUser(this.auth.accessToken);
      this.auth = saveAuthState({
        accessToken: this.auth.accessToken,
        tokenType: this.auth.tokenType,
        expiresIn: this.auth.expiresIn,
        user: user
      });
    },
    async confirmVideoPointCost() {
      var self = this;

      try {
        await this.ensurePointCosts();

        if (!this.videoPointCost) {
          this.videoStatus = "视频积分消耗配置未启用，请稍后重试。";
          return false;
        }

        await this.runWithAuthRetry(async function() {
          await self.refreshCurrentUser();
        });
      } catch (error) {
        this.videoStatus = error.message || "积分信息加载失败，请稍后重试。";
        return false;
      }

      var cost = this.videoPointCost;
      var balance = this.currentPointsBalance;

      return new Promise(function(resolve) {
        self.pendingVideoCostConfirm = resolve;
        self.videoCostDialog = {
          visible: true,
          cost: cost,
          balance: balance,
          remaining: Math.max(balance - cost, 0)
        };
      });
    },
    resolveVideoCostConfirm: function resolveVideoCostConfirm(confirmed) {
      if (this.pendingVideoCostConfirm) {
        this.pendingVideoCostConfirm(confirmed === true);
      }

      this.pendingVideoCostConfirm = null;
      this.videoCostDialog = {
        visible: false,
        cost: 0,
        balance: 0,
        remaining: 0
      };
    },
    scenePointCostText: function scenePointCostText(scene) {
      var points = normalizePointsCost(this.pointCostMap[scene]);

      if (this.pointCostsLoading && !points) {
        return "正在加载积分消耗...";
      }

      return points ? "本次消耗积分：" + points : "本次消耗积分以生成结果为准";
    },
    async fetchConversationDetail(id) {
      this.loadingConversation = true;
      this.textStatus = "";

      try {
        var payload = await getAiConversationDetail(id, this.auth.accessToken);
        var source = unwrapPayload(payload);
        var normalized = normalizeConversationDetail(source, id);
        this.currentConversation = normalized.conversation;
        this.textMessages = normalized.messages;
      } finally {
        this.loadingConversation = false;
        this.scrollConversationToBottomOnce(id);
      }
    },
    async fetchImageDetail(id) {
      this.imageLoadingDetail = true;
      this.imageStatus = "";

      try {
        var payload = await getAiImageDetail(id, this.auth.accessToken);
        var source = unwrapPayload(payload);
        this.currentImageTask = normalizeImageDetail(source);
        this.imageReferenceUrls = normalizeImageReferenceUrls(this.currentImageTask.referenceImageUrls);
      } finally {
        this.imageLoadingDetail = false;
        this.scrollImageToBottomOnce(id);
      }
    },
    async fetchVideoDetail(id, options) {
      var settings = options || {};
      var nextTask = null;
      var shouldScroll = false;

      if (!settings.silent) {
        this.videoLoadingDetail = true;
        this.videoStatus = "";
      }

      try {
        var params = typeof settings.refresh === "boolean" ? {
          refresh: settings.refresh ? "true" : "false"
        } : null;
        var payload = await getAiVideoDetail(id, this.auth.accessToken, params);
        var source = unwrapPayload(payload);
        nextTask = normalizeVideoDetail(source);

        if (!settings.silent || hasVideoTaskChanged(this.currentVideoTask, nextTask)) {
          this.currentVideoTask = nextTask;
          this.currentVideoProjectId = nextTask && nextTask.projectId
            ? nextTask.projectId
            : this.currentVideoProjectId;
          this.videoImageUrls = normalizeVideoImageSlots(nextTask && nextTask.imageUrls);

          shouldScroll = true;
        }

        return nextTask;
      } finally {
        if (!settings.silent) {
          this.videoLoadingDetail = false;
        }

        if (shouldScroll && !settings.silent) {
          this.scrollVideoToBottomOnce(id);
        }
      }
    },
    async fetchSpeechDetail(id) {
      this.speechLoadingDetail = true;
      this.speechStatus = "";

      try {
        var payload = await getAiSpeechDetail(id, this.auth.accessToken);
        var source = unwrapPayload(payload);
        this.currentSpeechTask = normalizeSpeechDetail(source);
      } finally {
        this.speechLoadingDetail = false;
      }
    },
      async handlePrimaryAction() {
        if (this.mode === "picture") {
          this.currentImageTask = null;
          this.imageStatus = "";
          this.draft = "";
          this.imageReferenceUrls = [""];
          this.safePush({
            name: "chat-picture",
            query: {
              new: "1"
            }
          });
        return;
      }

        if (this.mode === "video") {
          this.stopVideoPolling();
          this.currentVideoTask = null;
          this.currentVideoProjectId = null;
          this.videoStatus = "";
          this.draft = "";
          this.videoImageUrls = ["", ""];
          this.safePush({
            name: "chat-video",
            query: {
              new: "1"
            }
        });
        return;
      }

        if (this.mode === "speech") {
          this.currentSpeechTask = null;
          this.speechStatus = "";
          this.draft = "";
          this.safePush({
            name: "chat-speech",
            query: {
              new: "1"
            }
          });
        return;
      }

      if (this.mode !== "text") {
        this.resetDraft();
        return;
      }

      try {
        await this.createAndOpenConversation();
      } catch (error) {
        this.textStatus = error.message || "创建会话失败，请稍后重试。";
      }
    },
    async handleGenerateImage() {
      var self = this;
      var prompt = this.trimmedDraft;
      var requestPrompt = buildImagePromptWithStyle(prompt, this.selectedImageStyleId);
      var referenceImageUrls = normalizeImageReferenceUrls(this.imageReferenceUrls);

      if (!prompt) {
        return;
      }

      if (!isValidImageSize(this.imageSize)) {
        this.imageStatus = "当前图片尺寸无效，请从预设比例中选择。";
        return;
      }

      this.generatingImage = true;
      this.imageStatus = "";

      try {
        await this.runWithAuthRetry(async function() {
          var payload = await createAiImage({
            projectId: self.currentImageTask && self.currentImageTask.id ? self.currentImageTask.id : null,
            prompt: requestPrompt,
            negativePrompt: self.imageNegativePrompt,
            referenceImageUrls: referenceImageUrls.length ? referenceImageUrls : undefined,
            imageSize: self.imageSize,
            imageCount: self.imageCount,
            isPublic: false,
            watermark: false,
            provider: "doubao",
            model: "doubao-seedream-5-0-260128"
          }, self.auth.accessToken);
          var source = unwrapPayload(payload);
          var task = normalizeImageDetail(source);

          self.currentImageTask = task;
          self.upsertImageTask(task);
          self.scrollImageToBottomOnce(task.id);
          self.draft = "";
          self.imageReferenceUrls = normalizeImageReferenceUrls(task.referenceImageUrls.length ? task.referenceImageUrls : referenceImageUrls);
          self.$router.push({
            name: "chat-picture",
            params: {
              id: String(task.id)
            },
            query: {}
          });

          if (task.status === "failed") {
            self.imageStatus = task.errorMessage || "图片生成失败，请稍后重试。";
            return;
          }

          if (!normalizeImageAssets(task).length) {
            await self.fetchImageDetail(task.id);
          }

          self.upsertImageTask(self.currentImageTask);
        });
      } catch (error) {
        this.imageStatus = error.message || "图片生成失败，请稍后重试。";
      } finally {
        this.generatingImage = false;
      }
    },
    async handleGenerateVideo() {
      var self = this;
      var prompt = this.trimmedDraft;

      if (!prompt) {
        return;
      }

      if (!isValidVideoDuration(this.videoDuration)) {
        this.videoStatus = "视频时长无效，请选择 5 到 10 秒。";
        return;
      }

      var imageUrls = normalizeVideoPayloadImageUrls(this.videoImageUrls);
      var confirmed = await this.confirmVideoPointCost();

      if (!confirmed) {
        return;
      }

      this.generatingVideo = true;
      this.videoStatus = "";
      this.stopVideoPolling();

      try {
        await this.runWithAuthRetry(async function() {
          var payload = await createAiVideo({
            projectId: self.currentVideoProjectId || null,
            prompt: prompt,
            duration: self.videoDuration,
            ratio: self.videoRatio,
            resolution: self.videoResolution,
            watermark: self.videoWatermark,
            imageUrls: imageUrls.length ? imageUrls : undefined,
            isPublic: false,
            provider: "doubao",
            model: self.videoModal
          }, self.auth.accessToken);
          var source = unwrapPayload(payload);
          var task = normalizeVideoDetail(source);

          self.currentVideoTask = task;
          self.currentVideoProjectId = task && task.projectId ? task.projectId : self.currentVideoProjectId;
          self.upsertVideoTask(task);
          self.scrollVideoToBottomOnce(task.projectId || task.id);
          self.draft = "";
          self.videoImageUrls = normalizeVideoImageSlots(task && task.imageUrls ? task.imageUrls : imageUrls);
          await self.safePush({
            name: "chat-video",
            params: {
              id: String(task.projectId || task.id)
            },
            query: {}
          });

          if (shouldStopVideoPolling(task) && (task.status === "failed" || task.status === "canceled")) {
            self.videoStatus = task.errorMessage || "视频生成失败，请稍后重试。";
            return;
          }

          if (!shouldStopVideoPolling(task)) {
            self.videoStatus = "已提交到视频服务，正在获取生成结果...";
          }

          var refreshedTask = await self.fetchVideoDetail(task.projectId || task.id, { silent: true, refresh: true });
          if (hasVideoTaskChanged(task, refreshedTask)) {
            self.upsertVideoTask(refreshedTask);
          }
          self.maybeStartVideoPolling(task.projectId || task.id);
        });
      } catch (error) {
        this.videoStatus = error.message || "视频生成失败，请稍后重试。";
      } finally {
        this.generatingVideo = false;
      }
    },
    async handleGenerateSpeech() {
      var self = this;
      var text = this.trimmedDraft;

      if (!text) {
        return;
      }

      this.generatingSpeech = true;
      this.speechStatus = "";

      try {
        await this.runWithAuthRetry(async function() {
          var payload = await createAiSpeech({
            text: text,
            voiceType: self.speechVoiceType,
            encoding: self.speechEncoding,
            speedRatio: self.speechSpeedRatio,
            volumeRatio: self.speechVolumeRatio,
            pitchRatio: self.speechPitchRatio
          }, self.auth.accessToken);
          var source = unwrapPayload(payload);
          var task = normalizeSpeechDetail(source);

          self.currentSpeechTask = task;
          self.upsertSpeechTask(task);
          self.draft = "";
          self.$router.push({
            name: "chat-speech",
            params: {
              id: String(task.id)
            },
            query: {}
          });

          if (!task.audioUrl) {
            await self.fetchSpeechDetail(task.id);
            self.upsertSpeechTask(self.currentSpeechTask);
          }
        });
      } catch (error) {
        this.speechStatus = error.message || "语音生成失败，请稍后重试。";
      } finally {
        this.generatingSpeech = false;
      }
    },
    async openVideoReferencePicker(slotIndex) {
      this.videoReferencePicker = {
        visible: true,
        slotIndex: slotIndex
      };

      if (!this.videoReferenceCandidates.length) {
        await this.loadVideoReferenceCandidates();
      }
    },
    closeVideoReferencePicker: function closeVideoReferencePicker() {
      this.videoReferencePicker = {
        visible: false,
        slotIndex: 0
      };
    },
    async openImageReferencePicker(slotIndex) {
      this.imageReferencePicker = {
        visible: true,
        slotIndex: slotIndex
      };

      if (!this.imageReferenceCandidates.length) {
        await this.loadImageReferenceCandidates();
      }
    },
    closeImageReferencePicker: function closeImageReferencePicker() {
      this.imageReferencePicker = {
        visible: false,
        slotIndex: 0
      };
    },
    clearImageReferenceUrl: function clearImageReferenceUrl(index) {
      this.$set(this.imageReferenceUrls, index, "");
    },
    async handleImageReferenceFileChange(index, event) {
      var input = event && event.target;
      var file = input && input.files && input.files[0] ? input.files[0] : null;

      if (!file) {
        return;
      }

      if (!isValidVideoReferenceFile(file)) {
        this.imageStatus = "参考图片仅支持 jpg/jpeg/png/webp，且大小不能超过 10MB。";
        input.value = "";
        return;
      }

      this.$set(this.imageReferenceUploading, index, true);
      this.imageStatus = "";

      try {
        var payload = await this.runWithAuthRetry(() => uploadAiImageFile(file, this.auth.accessToken));
        var source = unwrapPayload(payload);
        var imageUrl = source && source.url ? String(source.url).trim() : "";

        if (!imageUrl) {
          throw new Error("图片上传成功，但未返回可用地址。");
        }

        this.$set(this.imageReferenceUrls, index, imageUrl);
      } catch (error) {
        this.imageStatus = error.message || "参考图片上传失败，请稍后重试。";
      } finally {
        this.$set(this.imageReferenceUploading, index, false);
        input.value = "";
      }
    },
    async loadImageReferenceCandidates() {
      this.imageReferenceLoading = true;

      try {
        if (!this.imageHistory.length) {
          await this.fetchImageHistory();
        }

        var ids = this.imageHistory.slice(0, 8).map(function(item) {
          return item.id;
        });
        var details = await Promise.all(ids.map((id) => getAiImageDetail(id, this.auth.accessToken)));
        var candidates = [];

        details.forEach(function(payload) {
          var task = normalizeImageDetail(unwrapPayload(payload));
          var assets = normalizeImageAssets(task);

          assets.forEach(function(asset) {
            candidates.push({
              id: asset.publicId,
              src: asset.src,
              title: asset.title,
              taskTitle: task.prompt || "图片任务"
            });
          });
        });

        this.imageReferenceCandidates = candidates.slice(0, 24);
      } catch (error) {
        this.imageStatus = error.message || "图片历史加载失败，请稍后重试。";
        this.imageReferenceCandidates = [];
      } finally {
        this.imageReferenceLoading = false;
      }
    },
    selectImageReferenceImage: function selectImageReferenceImage(item) {
      var index = this.imageReferencePicker.slotIndex || 0;
      this.$set(this.imageReferenceUrls, index, item.src);
      this.closeImageReferencePicker();
    },
    clearVideoImageUrl: function clearVideoImageUrl(index) {
      this.$set(this.videoImageUrls, index, "");
    },
    async handleVideoReferenceFileChange(index, event) {
      var input = event && event.target;
      var file = input && input.files && input.files[0] ? input.files[0] : null;

      if (!file) {
        return;
      }

      if (!isValidVideoReferenceFile(file)) {
        this.videoStatus = "参考图片仅支持 jpg/jpeg/png/webp，且大小不能超过 10MB。";
        input.value = "";
        return;
      }

      this.$set(this.videoImageUploading, index, true);
      this.videoStatus = "";

      try {
        var payload = await this.runWithAuthRetry(() => uploadAiImageFile(file, this.auth.accessToken));
        var source = unwrapPayload(payload);
        var imageUrl = source && source.url ? String(source.url).trim() : "";

        if (!imageUrl) {
          throw new Error("图片上传成功，但未返回可用地址。");
        }

        this.$set(this.videoImageUrls, index, imageUrl);
      } catch (error) {
        this.videoStatus = error.message || "参考图片上传失败，请稍后重试。";
      } finally {
        this.$set(this.videoImageUploading, index, false);
        input.value = "";
      }
    },
    async loadVideoReferenceCandidates() {
      this.videoReferenceLoading = true;

      try {
        if (!this.imageHistory.length) {
          await this.fetchImageHistory();
        }

        var ids = this.imageHistory.slice(0, 8).map(function(item) {
          return item.id;
        });
        var details = await Promise.all(ids.map((id) => getAiImageDetail(id, this.auth.accessToken)));
        var candidates = [];

        details.forEach(function(payload) {
          var task = normalizeImageDetail(unwrapPayload(payload));
          var assets = normalizeImageAssets(task);

          assets.forEach(function(asset) {
            candidates.push({
              id: asset.publicId,
              src: asset.src,
              title: asset.title,
              taskTitle: task.prompt || "图片任务"
            });
          });
        });

        this.videoReferenceCandidates = candidates.slice(0, 24);
      } catch (error) {
        this.videoStatus = error.message || "图片历史加载失败，请稍后重试。";
        this.videoReferenceCandidates = [];
      } finally {
        this.videoReferenceLoading = false;
      }
    },
    selectVideoReferenceImage: function selectVideoReferenceImage(item) {
      var index = this.videoReferencePicker.slotIndex || 0;
      this.$set(this.videoImageUrls, index, item.src);
      this.closeVideoReferencePicker();
    },
    async createAndOpenConversation() {
      var self = this;

      this.creatingConversation = true;
      this.textStatus = "";

      try {
        await this.runWithAuthRetry(async function() {
          var payload = await createAiConversation({
            provider: "deepseek",
            model: "deepseek-chat",
            title: "新对话"
          }, self.auth.accessToken);
          var source = unwrapPayload(payload);
          var conversation = normalizeConversationSummary(source);
          self.currentConversation = conversation;
          self.textMessages = [];
          self.upsertConversation(conversation);
          self.$router.push({
            name: "chat-text",
            params: {
              id: String(conversation.id)
            }
          });
        });
      } finally {
        this.creatingConversation = false;
      }
    },
    async handleSendTextMessage() {
      var self = this;
      var content = this.trimmedDraft;

      if (!content) {
        return;
      }

      this.sendingMessage = true;
      this.streamingMessageId = null;
      this.textStatus = "";

      try {
        await this.runWithAuthRetry(async function() {
          var conversation = self.currentConversation;
          var conversationId = self.currentConversationId;

          if (!conversationId) {
            conversation = await self.createConversationForSending();
            conversationId = String(conversation.id);
          } else if (!conversation || !conversation.id) {
            conversation = normalizeConversationSummary({
              id: conversationId,
              title: self.currentConversation && self.currentConversation.title
                ? self.currentConversation.title
                : "当前会话"
            });
            self.currentConversation = conversation;
          }

          var now = new Date().toISOString();
          var userMessage = {
            id: "temp-user-" + Date.now(),
            role: "user",
            content: content,
            time: formatDateTime(now),
            createdAt: now
          };
          var assistantMessage = {
            id: "temp-ai-" + Date.now(),
            role: "assistant",
            content: "",
            time: formatDateTime(now),
            createdAt: now
          };

          self.textMessages.push(userMessage);
          self.textMessages.push(assistantMessage);
          self.streamingMessageId = assistantMessage.id;
          self.draft = "";
          self.scrollConversationToBottomOnce(conversationId + "-sending-" + userMessage.id);

          await streamAiConversationMessage(conversationId, {
            content: content
          }, self.auth.accessToken, {
            onDelta: function(payload) {
              assistantMessage.content = payload.fullContent || (assistantMessage.content + (payload.content || ""));
            },
            onDone: function(payload) {
              var finalUserMessage = normalizeMessage(payload.userMessage);
              var finalAssistantMessage = normalizeMessage(payload.assistantMessage);
              var updatedConversation = normalizeConversationSummary(payload.conversation || conversation);
              var assistantIndex = self.textMessages.findIndex(function(item) {
                return item.id === assistantMessage.id;
              });
              var userIndex = self.textMessages.findIndex(function(item) {
                return item.id === userMessage.id;
              });

              if (userIndex > -1 && finalUserMessage) {
                self.$set(self.textMessages, userIndex, finalUserMessage);
              }

              if (assistantIndex > -1 && finalAssistantMessage) {
                self.$set(self.textMessages, assistantIndex, finalAssistantMessage);
              }

              self.currentConversation = updatedConversation;
              self.upsertConversation(updatedConversation);
              self.streamingMessageId = null;
            },
            onError: function(payload) {
              assistantMessage.content = payload.message || "生成失败，请稍后重试。";
              self.streamingMessageId = null;
              throw new Error(payload.message || "流式生成失败");
            }
          });
        });
      } catch (error) {
        this.textStatus = error.message || "发送消息失败，请稍后重试。";
        this.streamingMessageId = null;
      } finally {
        this.sendingMessage = false;
      }
    },
    async createConversationForSending() {
      var payload = await createAiConversation({
        provider: "deepseek",
        model: "deepseek-chat",
        title: "新对话"
      }, this.auth.accessToken);
      var source = unwrapPayload(payload);
      var conversation = normalizeConversationSummary(source);

      this.currentConversation = conversation;
      this.upsertConversation(conversation);
      this.$router.replace({
        name: "chat-text",
        params: {
          id: String(conversation.id)
        }
      });

      return conversation;
    },
    async runWithAuthRetry(task) {
      try {
        return await task();
      } catch (error) {
        if (error.status !== 401) {
          throw error;
        }

        var refreshed = await this.tryRefresh();
        if (!refreshed) {
          this.handleUnauthorized();
          throw error;
        }

        return task();
      }
    },
    async tryRefresh() {
      try {
        var payload = await refreshToken(this.auth.accessToken);
        this.auth = saveAuthState({
          accessToken: payload.access_token,
          tokenType: payload.token_type,
          expiresIn: payload.expires_in,
          user: payload.user
        });
        return true;
      } catch (error) {
        return false;
      }
    },
      handleUnauthorized: function handleUnauthorized() {
        clearAuthState();
        this.$router.replace({ name: "login" });
      },
      safePush: function safePush(location) {
        var resolved = this.$router.resolve(location).route;

        if (resolved.fullPath === this.$route.fullPath) {
          return Promise.resolve(this.$route);
        }

        return this.$router.push(location).catch(function(error) {
          if (error && error.name === "NavigationDuplicated") {
            return error;
          }

          throw error;
        });
      },
      handleHistoryClick: async function handleHistoryClick(item) {
        if (this.mode === "text") {
          this.safePush({
            name: "chat-text",
            params: {
              id: String(item.id)
            }
          });
        return;
      }

        if (this.mode === "picture") {
          this.safePush({
            name: "chat-picture",
            params: {
              id: String(item.id)
            }
          });
        return;
      }

        if (this.mode === "video") {
          var videoId = String(item.projectId || item.id);

          try {
            await this.safePush({
              name: "chat-video",
              params: {
                id: videoId
              }
            });
            await this.fetchVideoDetail(videoId, { refresh: false });
            this.resumeVideoPollingIfNeeded(videoId);
          } catch (error) {
            this.videoStatus = error.message || "视频项目加载失败，请稍后重试。";
          }
          return;
        }

        if (this.mode === "speech") {
          this.safePush({
            name: "chat-speech",
            params: {
              id: String(item.id)
            }
          });
        return;
      }

        this.draft = item.title;
      },
      async handleDeleteHistory(item) {
        var self = this;
        var id = item && item.id ? String(item.id) : "";
        var title = item && item.title ? item.title : "当前记录";

        if (!id || this.deletingHistoryId === id) {
          return;
        }

        if (!window.confirm('确认删除“' + title + '”吗？')) {
          return;
        }

        this.deletingHistoryId = id;

        try {
          await this.runWithAuthRetry(async function() {
            if (self.mode === "text") {
              await deleteAiConversation(id, self.auth.accessToken);
              await self.fetchTextHistory();
              await self.handleDeletedSelection(id, self.textHistory, "chat-text");
              return;
            }

            if (self.mode === "picture") {
              await deleteAiImage(id, self.auth.accessToken);
              await self.fetchImageHistory();
              await self.handleDeletedSelection(id, self.imageHistory, "chat-picture");
              return;
            }

            if (self.mode === "video") {
              await deleteAiVideo(id, self.auth.accessToken);
              await self.fetchVideoHistory();
              await self.handleDeletedSelection(id, self.videoHistory, "chat-video");
              return;
            }

            if (self.mode === "speech") {
              await deleteAiSpeech(id, self.auth.accessToken);
              await self.fetchSpeechHistory();
              await self.handleDeletedSelection(id, self.speechHistory, "chat-speech");
            }
          });
        } catch (error) {
          if (this.mode === "text") {
            this.textStatus = error.message || "删除会话失败，请稍后重试。";
          } else if (this.mode === "picture") {
            this.imageStatus = error.message || "删除图片任务失败，请稍后重试。";
          } else if (this.mode === "video") {
            this.videoStatus = error.message || "删除视频项目失败，请稍后重试。";
          } else if (this.mode === "speech") {
            this.speechStatus = error.message || "删除语音任务失败，请稍后重试。";
          }
        } finally {
          this.deletingHistoryId = null;
        }
      },
      async handleDeletedSelection(deletedId, list, routeName) {
        if (routeName === "chat-text") {
          this.currentConversation = null;
          this.textMessages = [];
          this.textStatus = "";
        }

        if (routeName === "chat-picture") {
          this.currentImageTask = null;
          this.imageStatus = "";
        }

        if (routeName === "chat-video") {
          this.stopVideoPolling();
          this.currentVideoTask = null;
          this.currentVideoProjectId = null;
          this.videoStatus = "";
        }

        if (routeName === "chat-speech") {
          this.currentSpeechTask = null;
          this.speechStatus = "";
        }

        if (list.length) {
          var nextId = String(list[0].projectId || list[0].id);

          await this.$router.replace({
            name: routeName,
            params: {
              id: nextId
            },
            query: {}
          });

          if (routeName === "chat-text") {
            await this.fetchConversationDetail(nextId);
            return;
          }

          if (routeName === "chat-picture") {
            await this.fetchImageDetail(nextId);
            return;
          }

          if (routeName === "chat-video") {
            await this.fetchVideoDetail(nextId, { refresh: false });
            this.resumeVideoPollingIfNeeded(nextId);
            return;
          }

          if (routeName === "chat-speech") {
            await this.fetchSpeechDetail(nextId);
            return;
          }

          return;
        }

        await this.$router.replace({
          name: routeName,
          query: {
            new: "1"
          }
        });
      },
      isActiveHistory: function isActiveHistory(item) {
        if (this.mode === "text") {
          return this.currentConversation
          && String(this.currentConversation.id) === String(item.id);
      }

      if (this.mode === "picture") {
        return this.currentImageTask
          && String(this.currentImageTask.id) === String(item.id);
      }

      if (this.mode === "video") {
        return this.currentVideoTask
          && String(this.currentVideoTask.projectId || this.currentVideoTask.id) === String(item.projectId || item.id);
      }

      if (this.mode === "speech") {
        return this.currentSpeechTask
          && String(this.currentSpeechTask.id) === String(item.id);
      }

      return false;
    },
    maybeStartVideoPolling: function maybeStartVideoPolling(task) {
      var self = this;
      var currentTaskId = task && typeof task === "object" ? task.id : task;

      this.stopVideoPolling();

      if (!currentTaskId) {
        return;
      }

      function scheduleNextPoll() {
        self.videoPollTimer = setTimeout(poll, 1000);
      }

      function poll() {
        self.runWithAuthRetry(async function() {
          var previousTask = self.currentVideoTask;
          var refreshedTask = await self.fetchVideoDetail(currentTaskId, { silent: true, refresh: true });

          if (hasVideoTaskChanged(previousTask, refreshedTask)) {
            self.upsertVideoTask(refreshedTask);
          }

          if (shouldStopVideoPolling(self.currentVideoTask)) {
            self.stopVideoPolling();

            if (self.currentVideoTask && self.currentVideoTask.status === "failed") {
              self.videoStatus = self.currentVideoTask.errorMessage || "视频生成失败，请稍后重试。";
            }

            return;
          }

          scheduleNextPoll();
        }).catch(function(error) {
          self.videoStatus = error.message || "视频任务刷新失败，请稍后重试。";
          self.stopVideoPolling();
        });
      }

      poll();
    },
    resumeVideoPollingIfNeeded: function resumeVideoPollingIfNeeded(task) {
      var currentTaskId = task && typeof task === "object" ? task.id : task;

      if (!currentTaskId || !this.currentVideoTask || shouldStopVideoPolling(this.currentVideoTask)) {
        return;
      }

      this.videoStatus = "视频仍在生成中，正在获取生成结果...";
      this.maybeStartVideoPolling(currentTaskId);
    },
    stopVideoPolling: function stopVideoPolling() {
      if (this.videoPollTimer) {
        clearTimeout(this.videoPollTimer);
        this.videoPollTimer = null;
      }
    },
    scrollConversationToBottom: function scrollConversationToBottom() {
      this.scrollRefToBottom("conversationBody", "conversationBottom");
    },
    scrollConversationToBottomOnce: function scrollConversationToBottomOnce(key) {
      this.scrollRefToBottomOnce("text", key, "conversationBody", "conversationBottom");
    },
    scrollImageToBottom: function scrollImageToBottom() {
      this.scrollRefToBottom("imageBody", "imageBottom");
    },
    scrollImageToBottomOnce: function scrollImageToBottomOnce(key) {
      this.scrollRefToBottomOnce("picture", key, "imageBody", "imageBottom");
    },
    scrollVideoToBottom: function scrollVideoToBottom() {
      this.scrollRefToBottom("videoBody", "videoBottom");
    },
    scrollVideoToBottomOnce: function scrollVideoToBottomOnce(key) {
      this.scrollRefToBottomOnce("video", key, "videoBody", "videoBottom");
    },
    scrollRefToBottomOnce: function scrollRefToBottomOnce(scope, key, refName, anchorRefName) {
      var scrollKey = scope + ":" + String(key || "default");

      if (this.autoScrolledKeys[scrollKey]) {
        return;
      }

      this.$set(this.autoScrolledKeys, scrollKey, true);
      this.scrollRefToBottom(refName, anchorRefName);
    },
    scrollRefToBottom: function scrollRefToBottom(refName, anchorRefName) {
      var self = this;

      this.$nextTick(function() {
        var container = self.$refs[refName];
        var anchor = anchorRefName ? self.$refs[anchorRefName] : null;

        if (!container) {
          return;
        }

        function applyScroll() {
          var scrollTarget = findScrollableParent(container) || container;

          if (anchor && typeof anchor.scrollIntoView === "function") {
            anchor.scrollIntoView({
              block: "end",
              inline: "nearest",
              behavior: "auto"
            });
          }

          if (typeof scrollTarget.scrollTo === "function") {
            scrollTarget.scrollTo({
              top: scrollTarget.scrollHeight,
              behavior: "auto"
            });
            return;
          }

          scrollTarget.scrollTop = scrollTarget.scrollHeight;
        }

        applyScroll();

        if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
          window.requestAnimationFrame(applyScroll);
        }

        setTimeout(applyScroll, 80);
        setTimeout(applyScroll, 260);
      });
    },
    renderMessageContent: function renderMessageContent(message) {
      var content = message && message.content ? message.content : "";

      return hasMarkdownSyntax(content) ? renderMarkdown(content) : renderPlainText(content);
    },
    resolveVideoMimeType: function resolveVideoMimeTypeMethod(url) {
      return resolveVideoMimeType(url);
    },
    isVideoPlaybackError: function isVideoPlaybackError(item) {
      var key = buildVideoPlaybackKey(item);

      return key ? this.videoPlaybackErrors[key] === true : false;
    },
    handleVideoPlaybackError: function handleVideoPlaybackError(item) {
      var key = buildVideoPlaybackKey(item);

      if (!key) {
        return;
      }

      this.$set(this.videoPlaybackErrors, key, true);
    },
    buildVideoDownloadName: function buildVideoDownloadNameMethod(item) {
      return buildVideoDownloadName(item);
    },
    formatPointsCost: function formatPointsCost(pointsCost) {
      return buildPointsCostText(pointsCost);
    },
    openImagePreview: function openImagePreview(item) {
      this.previewImage = item || null;
    },
    closeImagePreview: function closeImagePreview() {
      this.previewImage = null;
    },
    toggleImageAssetVisibility: function toggleImageAssetVisibility(item) {
      var self = this;

      if (!item || !this.currentImageTask || !this.currentImageTask.id || !(item.messageId || item.id)) {
        return;
      }

      this.runWithAuthRetry(async function() {
        var nextValue = !item.isPublic;
        var projectId = self.currentImageTask.id;
        var messageId = item.messageId || item.id;
        var payload = await updateAiImageVisibility(projectId, messageId, {
          isPublic: nextValue
        }, self.auth.accessToken);
        var source = unwrapPayload(payload);
        var nextTask = null;

        if (source && Object.keys(source).length) {
          nextTask = normalizeImageDetail(source);
        } else {
          await self.fetchImageDetail(projectId);
          nextTask = self.currentImageTask;
        }

        self.currentImageTask = nextTask;
        self.upsertImageTask(nextTask);
      }).catch(function(error) {
        self.imageStatus = error.message || "修改图片公开状态失败，请稍后重试。";
      });
    },
    toggleVideoMessageVisibility: function toggleVideoMessageVisibility(item) {
      var self = this;

      if (!item || !this.currentVideoTask || !(this.currentVideoTask.projectId || this.currentVideoTask.id) || !(item.messageId || item.id)) {
        return;
      }

      this.runWithAuthRetry(async function() {
        var projectId = self.currentVideoTask.projectId || self.currentVideoTask.id;
        var messageId = item.messageId || item.id;
        var nextValue = !item.isPublic;
        var payload = await updateAiVideoVisibility(projectId, messageId, {
          isPublic: nextValue
        }, self.auth.accessToken);
        var source = unwrapPayload(payload);
        var nextTask = null;

        if (source && Object.keys(source).length) {
          nextTask = normalizeVideoDetail(source);
        } else {
          nextTask = await self.fetchVideoDetail(projectId, { silent: true, refresh: false });
        }

        self.currentVideoTask = nextTask;
        self.upsertVideoTask(nextTask);
      }).catch(function(error) {
        self.videoStatus = error.message || "修改视频公开状态失败，请稍后重试。";
      });
    },
    upsertImageTask: function upsertImageTask(task) {
      var list = this.imageHistory.slice();
      var nextTask = normalizeImageSummary(task);
      var index = list.findIndex(function(item) {
        return String(item.id) === String(nextTask.id);
      });

      if (index > -1) {
        list.splice(index, 1);
      }

      list.unshift(nextTask);
      this.imageHistory = list;
    },
    upsertVideoTask: function upsertVideoTask(task) {
      var list = this.videoHistory.slice();
      var nextTask = normalizeVideoSummary(task);
      var index = list.findIndex(function(item) {
        return String(item.projectId || item.id) === String(nextTask.projectId || nextTask.id);
      });

      if (index > -1) {
        list.splice(index, 1);
      }

      list.unshift(nextTask);
      this.videoHistory = list;
    },
    upsertSpeechTask: function upsertSpeechTask(task) {
      var list = this.speechHistory.slice();
      var nextTask = normalizeSpeechSummary(task);
      var index = list.findIndex(function(item) {
        return String(item.id) === String(nextTask.id);
      });

      if (index > -1) {
        list.splice(index, 1);
      }

      list.unshift(nextTask);
      this.speechHistory = list;
    },
    upsertConversation: function upsertConversation(conversation) {
      var list = this.textHistory.slice();
      var nextConversation = normalizeConversationSummary(conversation);
      var index = list.findIndex(function(item) {
        return String(item.id) === String(nextConversation.id);
      });

      if (index > -1) {
        list.splice(index, 1);
      }

      list.unshift(nextConversation);
      this.textHistory = list;
    },
    applyChip: function applyChip(chip) {
      this.draft = chip;
    },
    resetDraft: function resetDraft() {
      this.draft = "";
    }
  }
};

function unwrapPayload(payload) {
  if (payload && payload.data && typeof payload.data === "object") {
    return payload.data;
  }

  return payload || {};
}

function normalizeConversationSummary(payload) {
  var source = payload || {};
  var lastMessage = source.lastMessage || null;
  var fallbackTitle = lastMessage && lastMessage.content
    ? String(lastMessage.content).slice(0, 30)
    : "Conversation";

  return {
    id: source.id || 0,
    provider: source.provider || "deepseek",
    model: source.model || "deepseek-chat",
    title: source.title || fallbackTitle,
    lastMessage: lastMessage,
    createdAt: source.createdAt || "",
    updatedAt: source.updatedAt || "",
    time: formatDateTime(source.updatedAt || source.createdAt)
  };
}

function normalizeConversationDetail(payload, fallbackId) {
  var source = payload || {};
  var conversation = withConversationId(source.conversation || source, fallbackId);
  var messages = Array.isArray(source.messages)
    ? source.messages
    : (Array.isArray(conversation.messages) ? conversation.messages : []);

  return {
    conversation: normalizeConversationSummary(conversation),
    messages: messages.map(normalizeMessage).filter(Boolean)
  };
}

function withConversationId(conversation, fallbackId) {
  var source = conversation || {};

  if (source.id || !fallbackId) {
    return source;
  }

  return Object.assign({}, source, {
    id: fallbackId
  });
}

function normalizeMessage(payload) {
  var source = payload || {};

  if (!source.id && !source.content) {
    return null;
  }

  return {
    id: source.id || "message-" + source.createdAt + "-" + source.role,
    role: source.role || "assistant",
    content: source.content || "",
    pointsCost: normalizePointsCost(source.pointsCost || source.points_cost),
    time: formatDateTime(source.createdAt),
    createdAt: source.createdAt || ""
  };
}

function normalizeImageSummary(payload) {
  var source = payload || {};
  var displayPrompt = stripImageStylePrompt(source.prompt || "");

  return {
    id: source.id || 0,
    title: displayPrompt || "图片任务",
    prompt: displayPrompt,
    status: source.status || "",
    statusText: resolveImageStatusText(source.status, source.statusText),
    isPublic: source.isPublic === true || source.is_public === true,
    imageCount: Number(source.imageCount || 0),
    updatedAt: source.updatedAt || source.createdAt || "",
    time: formatDateTime(source.updatedAt || source.createdAt)
  };
}

function normalizeImageDetail(payload) {
  var source = payload || {};
  var normalizedMessages = normalizeImageMessages(source);
  var latestMessage = normalizedMessages.length ? normalizedMessages[normalizedMessages.length - 1] : null;
  var displayPrompt = stripImageStylePrompt(source.prompt || "");

  return {
    id: source.id || 0,
    prompt: displayPrompt,
    negativePrompt: source.negativePrompt || "",
    referenceImageUrls: normalizeImageReferenceUrls(
      source.referenceImageUrls
      || source.reference_image_urls
      || (latestMessage && latestMessage.referenceImageUrls)
      || []
    ),
    imageSize: source.imageSize || "1:1",
    imageCount: Number(source.imageCount || 0),
    status: source.status || "",
    statusText: resolveImageStatusText(source.status, source.statusText),
    isPublic: source.isPublic === true || source.is_public === true,
    watermark: source.watermark === undefined ? false : !!source.watermark,
    errorMessage: source.errorMessage || "",
    assets: Array.isArray(source.assets) ? source.assets : [],
    messages: normalizedMessages,
    createdAt: source.createdAt || "",
    updatedAt: source.updatedAt || ""
  };
}

function normalizeImageMessages(payload) {
  var source = payload || {};
  var messages = Array.isArray(source.messages) ? source.messages : [];

  return messages.map(function(item) {
    return normalizeImageMessage(item, source);
  }).filter(Boolean);
}

function normalizeImageMessage(payload, task) {
  var source = payload || {};
  var parent = task || {};
  var assets = Array.isArray(source.assets) ? source.assets : [];
  var displayPrompt = stripImageStylePrompt(source.prompt || parent.prompt || "");

  if (!source.id && !assets.length && !source.prompt) {
    return null;
  }

  return {
    id: source.id || source.messageId || source.message_id || null,
    prompt: displayPrompt,
    referenceImageUrls: normalizeImageReferenceUrls(source.referenceImageUrls || source.reference_image_urls || []),
    status: source.status || parent.status || "",
    statusText: resolveImageStatusText(source.status || parent.status, source.statusText || parent.statusText),
    isPublic: source.isPublic === true || source.is_public === true,
    pointsCost: normalizePointsCost(source.pointsCost || source.points_cost || parent.pointsCost || parent.points_cost),
    createdAt: source.createdAt || parent.createdAt || "",
    updatedAt: source.updatedAt || source.createdAt || parent.updatedAt || parent.createdAt || "",
    assets: assets
  };
}

function normalizeVideoSummary(payload) {
  var source = payload || {};
  var messages = Array.isArray(source.messages) ? source.messages.map(normalizeVideoMessage).filter(Boolean) : [];
  var latestMessage = messages.length ? messages[messages.length - 1] : null;
  var normalizedStatus = normalizeVideoStatus(source.status || (latestMessage && latestMessage.status));

  return {
    id: source.id || 0,
    projectId: source.id || source.projectId || source.project_id || null,
    providerTaskId: source.providerTaskId || source.provider_task_id || (latestMessage ? latestMessage.providerTaskId : ""),
    title: source.title || source.prompt || (latestMessage && latestMessage.title) || "视频项目",
      prompt: source.prompt || (latestMessage && latestMessage.prompt) || "",
      status: normalizedStatus,
      statusText: resolveVideoStatusText(normalizedStatus, source.statusText || (latestMessage && latestMessage.statusText)),
      isPublic: source.isPublic === true || source.is_public === true,
      duration: Number(source.duration || (latestMessage && latestMessage.duration) || 0),
    ratio: source.ratio || (latestMessage && latestMessage.ratio) || "16:9",
    resolution: source.resolution || (latestMessage && latestMessage.resolution) || "720p",
    updatedAt: source.updatedAt || source.createdAt || "",
    time: formatDateTime(source.updatedAt || source.createdAt)
  };
}

function normalizeVideoDetail(payload) {
  var source = payload || {};
  var detailSource = source.project || source.video || source;
  var messageSource = resolveVideoMessageList(source, detailSource);
  var normalizedMessages = messageSource.map(normalizeVideoMessage).filter(Boolean);
  var latestMessage = normalizedMessages.length ? normalizedMessages[normalizedMessages.length - 1] : null;
  var normalizedStatus = normalizeVideoStatus(detailSource.status || source.status || (latestMessage && latestMessage.status));

  return {
    id: detailSource.id || source.id || 0,
    projectId: detailSource.id || detailSource.projectId || detailSource.project_id || source.projectId || source.project_id || null,
    providerTaskId: detailSource.providerTaskId || detailSource.provider_task_id || source.providerTaskId || source.provider_task_id || (latestMessage ? latestMessage.providerTaskId : ""),
    prompt: detailSource.prompt || source.prompt || (latestMessage && latestMessage.prompt) || "",
    duration: Number(detailSource.duration || source.duration || (latestMessage && latestMessage.duration) || 0),
    ratio: detailSource.ratio || source.ratio || (latestMessage && latestMessage.ratio) || "16:9",
    resolution: detailSource.resolution || source.resolution || (latestMessage && latestMessage.resolution) || "720p",
    watermark: !!(detailSource.watermark || source.watermark),
    provider: detailSource.provider || source.provider || "doubao",
    model: detailSource.model || source.model || "Doubao-Seedance-2.0-fast",
    generationMode: detailSource.generationMode || detailSource.generation_mode || source.generationMode || source.generation_mode || (latestMessage && latestMessage.generationMode) || "text_to_video",
    imageUrls: normalizeVideoPayloadImageUrls(detailSource.imageUrls || detailSource.image_urls || source.imageUrls || source.image_urls || (latestMessage && latestMessage.imageUrls) || []),
    status: normalizedStatus,
    statusText: resolveVideoStatusText(normalizedStatus, detailSource.statusText || source.statusText || (latestMessage && latestMessage.statusText)),
    isPublic: detailSource.isPublic === true || detailSource.is_public === true || source.isPublic === true || source.is_public === true,
    videoUrl: normalizeRemoteUrl(detailSource.videoUrl || source.videoUrl || (latestMessage && latestMessage.videoUrl) || ""),
    errorMessage: detailSource.errorMessage || source.errorMessage || (latestMessage && latestMessage.errorMessage) || "",
    messages: normalizedMessages,
    createdAt: detailSource.createdAt || source.createdAt || "",
    updatedAt: detailSource.updatedAt || source.updatedAt || ""
  };
}

function resolveVideoMessageList(source, detailSource) {
  var candidates = [
    source && source.messages,
    detailSource && detailSource.messages,
    source && source.videoMessages,
    source && source.video_messages,
    detailSource && detailSource.videoMessages,
    detailSource && detailSource.video_messages
  ];

  for (var index = 0; index < candidates.length; index += 1) {
    if (Array.isArray(candidates[index])) {
      return candidates[index];
    }
  }

  return [];
}

function normalizeVideoMessage(payload) {
  var source = payload || {};
  var normalizedStatus = normalizeVideoStatus(source.status);

  if (!source.id && !source.prompt && !source.videoUrl) {
    return null;
  }

    return {
      id: source.id || (source.providerTaskId || "video-message"),
      messageId: source.id || source.messageId || source.message_id || source.aiVideoMessageId || null,
      projectId: source.projectId || source.project_id || source.parentId || source.aiVideoProjectId || null,
      providerTaskId: source.providerTaskId || source.provider_task_id || "",
    title: source.prompt ? String(source.prompt).slice(0, 18) : "视频消息",
    prompt: source.prompt || "",
    duration: Number(source.duration || 0),
      durationLabel: source.duration ? (String(source.duration) + " 秒") : (source.resolution || ""),
    ratio: source.ratio || "16:9",
    resolution: source.resolution || "720p",
    status: normalizedStatus,
    statusText: resolveVideoStatusText(normalizedStatus, source.statusText),
    isPublic: source.isPublic === true || source.is_public === true,
    pointsCost: normalizePointsCost(source.pointsCost || source.points_cost),
    generationMode: source.generationMode || source.generation_mode || "text_to_video",
    imageUrls: normalizeVideoPayloadImageUrls(source.imageUrls || source.image_urls || []),
    videoUrl: normalizeRemoteUrl(source.videoUrl || ""),
    coverUrl: normalizeRemoteUrl(source.coverUrl || ""),
    errorMessage: source.errorMessage || "",
      createdAt: source.createdAt || "",
      updatedAt: source.updatedAt || source.createdAt || "",
      time: formatDateTime(source.createdAt || source.updatedAt)
    };
}

function normalizeVideoMessages(task) {
  var source = task || {};
  var messages = Array.isArray(source.messages) ? source.messages : [];
  var normalized = messages.map(normalizeVideoMessage).filter(Boolean).slice();

  if (normalized.length) {
    return normalized;
  }

  var current = normalizeVideoMessage(source);

  return current ? [current] : [];
}

function normalizeSpeechSummary(payload) {
  var source = payload || {};
  var latestMessage = source.latestMessage || source.latest_message || {};
  var text = source.text || latestMessage.text || source.title || "语音任务";

  return {
    id: source.id || latestMessage.projectId || latestMessage.speechId || 0,
    title: text ? String(text).slice(0, 24) : "语音任务",
    text: text || "",
    audioUrl: normalizeRemoteUrl(source.audioUrl || source.audio_url || latestMessage.audioUrl || latestMessage.audio_url || ""),
    pointsCost: normalizePointsCost(source.pointsCost || source.points_cost || latestMessage.pointsCost || latestMessage.points_cost),
    updatedAt: source.updatedAt || source.createdAt || latestMessage.createdAt || "",
    time: formatDateTime(source.updatedAt || source.createdAt || latestMessage.createdAt)
  };
}

function normalizeSpeechDetail(payload) {
  var source = payload || {};
  var latestMessage = source.latestMessage || source.latest_message || {};
  var summary = normalizeSpeechSummary(source);

  return Object.assign({}, summary, {
    text: source.text || latestMessage.text || summary.text || "",
    voiceType: source.voiceType || source.voice_type || latestMessage.voiceType || latestMessage.voice_type || "",
    encoding: source.encoding || latestMessage.encoding || "mp3",
    speedRatio: Number(source.speedRatio || source.speed_ratio || latestMessage.speedRatio || latestMessage.speed_ratio || 1),
    volumeRatio: Number(source.volumeRatio || source.volume_ratio || latestMessage.volumeRatio || latestMessage.volume_ratio || 1),
    pitchRatio: Number(source.pitchRatio || source.pitch_ratio || latestMessage.pitchRatio || latestMessage.pitch_ratio || 1),
    latestMessage: latestMessage
  });
}

function hasVideoTaskChanged(previousTask, nextTask) {
  if (!previousTask && nextTask) {
    return true;
  }

  if (previousTask && !nextTask) {
    return true;
  }

  if (!previousTask && !nextTask) {
    return false;
  }

  return JSON.stringify(buildVideoTaskSignature(previousTask)) !== JSON.stringify(buildVideoTaskSignature(nextTask));
}

function buildVideoTaskSignature(task) {
  var source = task || {};

  return {
    id: source.id || null,
    projectId: source.projectId || null,
    status: source.status || "",
    statusText: source.statusText || "",
    prompt: source.prompt || "",
    videoUrl: source.videoUrl || "",
    errorMessage: source.errorMessage || "",
    updatedAt: source.updatedAt || "",
    messages: normalizeVideoMessages(source).map(function(item) {
      return {
        id: item.id || null,
        status: item.status || "",
        statusText: item.statusText || "",
        videoUrl: item.videoUrl || "",
        errorMessage: item.errorMessage || "",
        pointsCost: item.pointsCost || 0,
        updatedAt: item.updatedAt || "",
        prompt: item.prompt || ""
      };
    })
  };
}

function normalizeImageAssets(task) {
  var source = task || {};
  var messages = Array.isArray(source.messages) ? source.messages : [];
  var flattenedAssets = [];

  if (messages.length) {
    messages.forEach(function(message) {
      var assets = Array.isArray(message.assets) ? message.assets : [];
      assets.forEach(function(asset, index) {
        flattenedAssets.push({
          asset: asset,
          index: index,
          message: message
        });
      });
    });
  } else {
    var fallbackAssets = Array.isArray(source.assets) ? source.assets : [];
    fallbackAssets.forEach(function(asset, index) {
      flattenedAssets.push({
        asset: asset,
        index: index,
        message: null
      });
    });
  }

  return flattenedAssets.map(function(entry) {
    var item = entry.asset;
    var index = entry.index;
    var message = entry.message;
    var src = resolveImageAssetSource(item);
    var publicId = buildImageAssetPublicId(source, item, index, message);
    var messageId = resolveImageAssetMessageId(source, item, message);

    if (!src) {
      return null;
    }

    return {
      id: item.id || "asset-" + index,
      messageId: messageId,
      publicId: publicId,
      isPublic: item.isPublic === true || item.is_public === true || (message && (message.isPublic === true || message.is_public === true)) || false,
      pointsCost: message && message.pointsCost
        ? message.pointsCost
        : normalizePointsCost(item.pointsCost || item.points_cost || source.pointsCost || source.points_cost),
      src: src,
      title: "生成结果 " + (index + 1),
      tag: item.revisedPrompt ? "优化提示词" : ((message && message.statusText) || source.statusText || "已生成"),
      prompt: stripImageStylePrompt(item.revisedPrompt || item.prompt || (message && message.prompt) || source.prompt || ""),
      createdAt: item.createdAt || (message && message.createdAt) || source.createdAt || "",
      updatedAt: item.updatedAt || (message && message.updatedAt) || source.updatedAt || source.createdAt || ""
    };
  }).filter(Boolean);
}

function buildImageAssetPublicId(task, asset, index, message) {
  var taskId = task && task.id ? String(task.id) : "image-task";
  var messageId = message && message.id ? String(message.id) : "message";
  var assetId = asset && (asset.id || asset.assetId || asset.fileId || asset.imageUrl || asset.imageBase64)
    ? String(asset.id || asset.assetId || asset.fileId || asset.imageUrl || asset.imageBase64)
    : "asset-" + index;

  return taskId + ":" + messageId + ":" + assetId;
}

function resolveImageAssetMessageId(task, asset, message) {
  var source = asset || {};
  var parent = task || {};
  var currentMessage = message || {};

  return currentMessage.id
    || currentMessage.messageId
    || currentMessage.message_id
    || source.messageId
    || source.message_id
    || source.aiImageMessageId
    || parent.messageId
    || parent.message_id
    || parent.latestMessageId
    || parent.latest_message_id
    || null;
}

function resolveImageAssetSource(asset) {
  var source = asset || {};

  if (source.imageUrl) {
    return normalizeRemoteUrl(source.imageUrl);
  }

  if (source.imageBase64) {
    if (source.imageBase64.indexOf("data:") === 0) {
      return source.imageBase64;
    }

    return "data:image/png;base64," + source.imageBase64;
  }

  return "";
}

function resolveImageStatusText(status, fallback) {
  if (fallback) {
    return fallback;
  }

  if (status === "completed" || status === "succeeded") {
    return "已完成";
  }

  if (status === "failed") {
    return "生成失败";
  }

  if (status === "processing" || status === "pending") {
    return "生成中";
  }

  return "等待生成";
}

function resolveVideoStatusText(status, fallback) {
  if (fallback) {
    return fallback;
  }

  if (status === "queued") {
    return "排队中";
  }

  if (status === "running") {
    return "生成中";
  }

  if (status === "succeeded") {
    return "已完成";
  }

  if (status === "failed") {
    return "生成失败";
  }

  if (status === "canceled") {
    return "已取消";
  }

  return "等待生成";
}

function shouldStopVideoPolling(task) {
  var source = task || {};
  var messages = normalizeVideoMessages(source);

  if (!messages.length) {
    var normalizedStatus = normalizeVideoStatus(source.status);
    return normalizedStatus === "failed"
      || normalizedStatus === "canceled"
      || normalizedStatus === "succeeded";
  }

  var hasPendingMessage = messages.some(function(message) {
    var status = normalizeVideoStatus(message.status);
    return status === "queued" || status === "running";
  });

  if (!hasPendingMessage) {
    return true;
  }

  return false;
}

function normalizeVideoStatus(status) {
  var value = typeof status === "string" ? status.toLowerCase().trim() : status;

  if (value === 0 || value === "0" || value === "pending" || value === "created" || value === "submitted" || value === "queued") {
    return "queued";
  }

  if (value === 1 || value === "1" || value === "running" || value === "processing" || value === "in_progress") {
    return "running";
  }

  if (value === 2 || value === "2" || value === "success" || value === "succeeded" || value === "completed") {
    return "succeeded";
  }

  if (value === 3 || value === "3" || value === "fail" || value === "failed" || value === "error") {
    return "failed";
  }

  if (value === 4 || value === "4" || value === "canceled" || value === "cancelled") {
    return "canceled";
  }

  return typeof value === "string" && value ? value : "queued";
}

function normalizeRemoteUrl(url) {
  if (!url) {
    return "";
  }

  return String(url);
}

function resolveVideoMimeType(url) {
  var cleanUrl = String(url || "").split("?")[0].split("#")[0].toLowerCase();

  if (/\.webm$/.test(cleanUrl)) {
    return "video/webm";
  }

  if (/\.ogg$|\.ogv$/.test(cleanUrl)) {
    return "video/ogg";
  }

  if (/\.m3u8$/.test(cleanUrl)) {
    return "application/vnd.apple.mpegurl";
  }

  if (/\.mov$/.test(cleanUrl)) {
    return "video/quicktime";
  }

  return "video/mp4";
}

function buildVideoPlaybackKey(item) {
  var source = item || {};

  return source.id || source.messageId || source.videoUrl || "";
}

function buildVideoDownloadName(item) {
  var source = item || {};
  var title = String(source.title || source.prompt || "video")
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .slice(0, 40);
  var cleanUrl = String(source.videoUrl || "").split("?")[0].split("#")[0].toLowerCase();
  var extensionMatch = cleanUrl.match(/\.([a-z0-9]+)$/);
  var extension = extensionMatch ? extensionMatch[1] : "mp4";

  return title + "." + extension;
}

function normalizeVideoPayloadImageUrls(value) {
  return (Array.isArray(value) ? value : [])
    .map(function(item) {
      return String(item || "").trim();
    })
    .filter(Boolean)
    .slice(0, 2);
}

function normalizeVideoImageSlots(value) {
  var list = normalizeVideoPayloadImageUrls(value);

  return [
    list[0] || "",
    list[1] || ""
  ];
}

function normalizeImageReferenceUrls(value) {
  return (Array.isArray(value) ? value : [])
    .map(function(item) {
      return String(item || "").trim();
    })
    .filter(Boolean)
    .slice(0, 1);
}

function findImageStylePreset(styleId) {
  return IMAGE_STYLE_PRESETS.find(function(style) {
    return style.id === styleId;
  }) || null;
}

function buildImagePromptWithStyle(prompt, styleId) {
  var basePrompt = String(prompt || "").trim();
  var style = findImageStylePreset(styleId);

  if (!style || !style.prompt) {
    return basePrompt;
  }

  return basePrompt + "，" + style.prompt;
}

function stripImageStylePrompt(value) {
  var output = String(value || "");

  IMAGE_STYLE_PRESETS.forEach(function(style) {
    if (!style.prompt) {
      return;
    }

    output = output.split(style.prompt).join("");
  });

  return output
    .replace(/[，,、\s]+$/g, "")
    .replace(/^[，,、\s]+/g, "")
    .replace(/[，,、]{2,}/g, "，")
    .trim();
}

function normalizePointsCost(value) {
  var points = Number(value);

  return isNaN(points) || points <= 0 ? 0 : points;
}

function normalizePointCostMap(payload) {
  var source = payload || {};
  var output = {};
  var costMap = source.costMap || source.cost_map || {};

  Object.keys(costMap).forEach(function(scene) {
    output[scene] = normalizePointsCost(costMap[scene]);
  });

  if (Array.isArray(source.costs)) {
    source.costs.forEach(function(item) {
      if (!item || !item.scene || item.enabled === false) {
        return;
      }

      output[item.scene] = normalizePointsCost(item.points);
    });
  }

  return output;
}

function findScrollableParent(element) {
  var current = element;

  while (current && current.parentElement) {
    if (current.scrollHeight > current.clientHeight + 2) {
      return current;
    }

    current = current.parentElement;
  }

  if (typeof document !== "undefined") {
    var scrollingElement = document.scrollingElement || document.documentElement;

    if (scrollingElement && scrollingElement.scrollHeight > scrollingElement.clientHeight + 2) {
      return scrollingElement;
    }
  }

  return element || null;
}

function buildPointsCostText(value) {
  var points = normalizePointsCost(value);

  return points ? "消耗积分：" + points : "";
}

function mergeVoiceDraft(base, transcript) {
  var prefix = String(base || "");
  var suffix = String(transcript || "").trim();

  if (!suffix) {
    return prefix;
  }

  return prefix + suffix;
}

function isValidImageSize(size) {
  return [
    "1:1",
    "16:9",
    "9:16",
    "720p",
    "1080p",
    "4:3",
    "3:4",
    "21:9"
  ].indexOf(String(size || "").trim()) > -1;
}

function isValidVideoDuration(duration) {
  return Number(duration) >= 5 && Number(duration) <= 10;
}

function isValidVideoReferenceFile(file) {
  if (!file) {
    return false;
  }

  var allowedTypes = ["image/jpeg", "image/png", "image/webp"];
  var lowerName = String(file.name || "").toLowerCase();
  var allowedExtension = /\.(jpg|jpeg|png|webp)$/.test(lowerName);

  return allowedExtension
    && allowedTypes.indexOf(file.type) > -1
    && Number(file.size || 0) <= 10 * 1024 * 1024;
}

function formatDateTime(value) {
  if (!value) {
    return "刚刚";
  }

  var date = new Date(value);

  if (isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function hasMarkdownSyntax(value) {
  var text = typeof value === "string" ? value : "";

  if (!text.trim()) {
    return false;
  }

  return /(^|\n)\s{0,3}#{1,6}\s+\S/.test(text)
    || /(^|\n)\s{0,3}([-*+]|\d+\.)\s+\S/.test(text)
    || /(^|\n)\s{0,3}>\s?\S/.test(text)
    || /```[\s\S]*```/.test(text)
    || /`[^`\n]+`/.test(text)
    || /\*\*[^*\n]+\*\*/.test(text)
    || /__[^_\n]+__/.test(text)
    || /\[[^\]\n]+\]\(https?:\/\/[^)\s]+\)/.test(text)
    || /(^|\n)\s*\|.+\|\s*\n\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*(\n|$)/.test(text)
    || /(^|\n)\s{0,3}([-*_])\s*(\2\s*){2,}($|\n)/.test(text)
    || /~~[^~\n]+~~/.test(text)
    || /(^|\n)\s{0,3}[-*+]\s+\[[ xX]\]\s+/.test(text);
}

function renderPlainText(value) {
  var text = typeof value === "string" ? value.replace(/\r\n/g, "\n") : "";

  if (!text) {
    return "";
  }

  return "<p>" + escapeHtml(text).replace(/\n/g, "<br>") + "</p>";
}

function renderMarkdown(value) {
  var text = typeof value === "string" ? value.replace(/\r\n/g, "\n") : "";
  var escaped = escapeHtml(text);
  var fenceStore = [];

  escaped = escaped.replace(/```([\w-]+)?\n?([\s\S]*?)```/g, function(match, language, code) {
    var token = "%%FENCE_" + fenceStore.length + "%%";
    var langClass = language ? " language-" + language : "";
    fenceStore.push(
      '<pre class="studio-markdown-pre"><code class="studio-markdown-code-block' + langClass + '">'
      + code.replace(/^\n+|\n+$/g, "")
      + "</code></pre>"
    );
    return token;
  });

  var lines = escaped.split("\n");
  var html = [];
  var paragraphLines = [];
  var listType = "";
  var listItems = [];
  var quoteLines = [];

  function flushParagraph() {
    if (!paragraphLines.length) {
      return;
    }

    html.push("<p>" + renderInline(paragraphLines.join("<br>")) + "</p>");
    paragraphLines = [];
  }

  function flushList() {
    if (!listItems.length || !listType) {
      listItems = [];
      listType = "";
      return;
    }

    html.push("<" + listType + ">" + listItems.join("") + "</" + listType + ">");
    listItems = [];
    listType = "";
  }

  function flushQuote() {
    if (!quoteLines.length) {
      return;
    }

    html.push("<blockquote><p>" + renderInline(quoteLines.join("<br>")) + "</p></blockquote>");
    quoteLines = [];
  }

  for (var lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
    var line = lines[lineIndex];
    var headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    var orderedMatch = line.match(/^\d+\.\s+(.*)$/);
    var unorderedMatch = line.match(/^[-*+]\s+(.*)$/);
    var taskMatch = line.match(/^[-*+]\s+\[([ xX])\]\s+(.*)$/);
    var quoteMatch = line.match(/^&gt;\s?(.*)$/);
    var horizontalRuleMatch = line.match(/^\s{0,3}([-*_])\s*(\1\s*){2,}$/);

    if (!line.trim()) {
      flushParagraph();
      flushList();
      flushQuote();
      continue;
    }

    if (line.indexOf("%%FENCE_") === 0 && line.indexOf("%%") === line.length - 2) {
      flushParagraph();
      flushList();
      flushQuote();
      html.push(line);
      continue;
    }

    if (isMarkdownTableStart(lines, lineIndex)) {
      flushParagraph();
      flushList();
      flushQuote();
      var tableResult = renderMarkdownTable(lines, lineIndex);
      html.push(tableResult.html);
      lineIndex = tableResult.nextIndex - 1;
      continue;
    }

    if (horizontalRuleMatch) {
      flushParagraph();
      flushList();
      flushQuote();
      html.push("<hr>");
      continue;
    }

    if (headingMatch) {
      flushParagraph();
      flushList();
      flushQuote();
      html.push("<h" + headingMatch[1].length + ">" + renderInline(headingMatch[2]) + "</h" + headingMatch[1].length + ">");
      continue;
    }

    if (quoteMatch) {
      flushParagraph();
      flushList();
      quoteLines.push(quoteMatch[1]);
      continue;
    }

    if (orderedMatch) {
      flushParagraph();
      flushQuote();
      if (listType && listType !== "ol") {
        flushList();
      }
      listType = "ol";
      listItems.push("<li>" + renderInline(orderedMatch[1]) + "</li>");
      continue;
    }

    if (taskMatch) {
      flushParagraph();
      flushQuote();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listItems.push(
        '<li class="studio-markdown-task"><input type="checkbox" disabled'
        + (taskMatch[1].toLowerCase() === "x" ? " checked" : "")
        + "> <span>" + renderInline(taskMatch[2]) + "</span></li>"
      );
      continue;
    }

    if (unorderedMatch) {
      flushParagraph();
      flushQuote();
      if (listType && listType !== "ul") {
        flushList();
      }
      listType = "ul";
      listItems.push("<li>" + renderInline(unorderedMatch[1]) + "</li>");
      continue;
    }

    flushList();
    flushQuote();
    paragraphLines.push(line);
  }

  flushParagraph();
  flushList();
  flushQuote();

  return restoreFenceTokens(html.join(""), fenceStore);
}

function renderInline(value) {
  var codeStore = [];
  var linkStore = [];
  var output = String(value || "");

  output = output.replace(/`([^`]+)`/g, function(match, code) {
    var token = "%%CODE_" + codeStore.length + "%%";
    codeStore.push("<code>" + code + "</code>");
    return token;
  });

  output = output.replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, function(match, label, href) {
    var token = "%%LINK_" + linkStore.length + "%%";
    linkStore.push('<a href="' + href + '" target="_blank" rel="noopener noreferrer">' + label + "</a>");
    return token;
  });

  output = output
    .replace(/~~([^~]+)~~/g, "<del>$1</del>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/__([^_]+)__/g, "<strong>$1</strong>")
    .replace(/\*([^*\n]+)\*/g, "<em>$1</em>")
    .replace(/_([^_\n]+)_/g, "<em>$1</em>")
    .replace(/(^|[\s(])((?:https?:\/\/)[^\s<]+[^<.,;:!?，。；：！？\s)])/g, '$1<a href="$2" target="_blank" rel="noopener noreferrer">$2</a>');

  output = output.replace(/%%LINK_(\d+)%%/g, function(match, index) {
    return linkStore[Number(index)] || "";
  });

  output = output.replace(/%%CODE_(\d+)%%/g, function(match, index) {
    return codeStore[Number(index)] || "";
  });

  return output;
}

function isMarkdownTableStart(lines, index) {
  return lines[index]
    && lines[index].indexOf("|") > -1
    && lines[index + 1]
    && /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(lines[index + 1]);
}

function renderMarkdownTable(lines, startIndex) {
  var headers = splitMarkdownTableRow(lines[startIndex]);
  var rows = [];
  var index = startIndex + 2;

  while (index < lines.length && lines[index].trim() && lines[index].indexOf("|") > -1) {
    rows.push(splitMarkdownTableRow(lines[index]));
    index += 1;
  }

  var headerHtml = headers.map(function(cell) {
    return "<th>" + renderInline(cell) + "</th>";
  }).join("");

  var bodyHtml = rows.map(function(row) {
    var cells = headers.map(function(header, cellIndex) {
      return "<td>" + renderInline(row[cellIndex] || "") + "</td>";
    }).join("");

    return "<tr>" + cells + "</tr>";
  }).join("");

  return {
    html: '<div class="studio-markdown-table-wrap"><table><thead><tr>' + headerHtml + "</tr></thead><tbody>" + bodyHtml + "</tbody></table></div>",
    nextIndex: index
  };
}

function splitMarkdownTableRow(row) {
  var normalized = String(row || "").trim();

  if (normalized.charAt(0) === "|") {
    normalized = normalized.slice(1);
  }

  if (normalized.charAt(normalized.length - 1) === "|") {
    normalized = normalized.slice(0, -1);
  }

  return normalized.split("|").map(function(cell) {
    return cell.trim();
  });
}

function restoreFenceTokens(value, fenceStore) {
  return value.replace(/%%FENCE_(\d+)%%/g, function(match, index) {
    return fenceStore[Number(index)] || "";
  });
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
</script>

<style scoped>
.studio-picture-preview-trigger {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: zoom-in;
}

.studio-picture-overlay {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.studio-lightbox {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: rgba(10, 14, 24, 0.82);
  backdrop-filter: blur(10px);
}

.studio-lightbox-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(100%, 1100px);
  max-height: 100%;
  overflow: auto;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(18, 24, 36, 0.98), rgba(9, 13, 22, 0.98));
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.38);
}

.studio-lightbox-media {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  padding: 1.5rem 1.5rem 0;
  background: #0b1018;
}

.studio-lightbox-image {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: 78vh;
  margin: 0 auto;
  object-fit: contain;
  background: #0b1018;
}

.studio-lightbox-copy {
  display: grid;
  gap: 0.5rem;
  padding: 1.25rem 1.5rem 1.5rem;
  color: #f4f7fb;
}

.studio-lightbox-copy strong {
  font-size: 1rem;
}

.studio-lightbox-copy span {
  color: rgba(203, 214, 229, 0.86);
  font-size: 0.82rem;
}

.studio-lightbox-copy p {
  margin: 0;
  color: rgba(232, 238, 247, 0.92);
  line-height: 1.65;
  white-space: pre-wrap;
}

.studio-lightbox-close {
  position: absolute;
  top: 1.4rem;
  right: 1.4rem;
  z-index: 2;
  border: 0;
  border-radius: 999px;
  padding: 0.7rem 1rem;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;
  backdrop-filter: blur(8px);
}

.studio-lightbox-close:hover {
  background: rgba(255, 255, 255, 0.22);
}

@media (max-width: 768px) {
  .studio-lightbox {
    padding: 1rem;
  }

  .studio-lightbox-panel {
    border-radius: 18px;
  }

  .studio-lightbox-media {
    min-height: 32vh;
    padding: 1rem 1rem 0;
  }

  .studio-lightbox-image {
    max-height: 64vh;
  }
}

.studio-message-streaming .studio-bubble {
  position: relative;
}

.studio-typing-cursor {
  display: inline-block;
  width: 0.55em;
  height: 1.1em;
  margin-left: 0.15em;
  vertical-align: text-bottom;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.9;
  animation: studioCursorBlink 1s steps(1) infinite;
}

@keyframes studioCursorBlink {
  0%, 49% {
    opacity: 0.9;
  }

  50%, 100% {
    opacity: 0.15;
  }
}
</style>





